import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { SAGA_ACTIONS, URLS } from "../../_config";
import { useCommonApi } from "../../_common/hooks/actions/common/appCommonApiHook";
import { useJobPostingApi } from "../../_common/hooks/actions/jobPosting/appJobPostingApiHook";
import { useToaster } from "../../_common/hooks/actions/common/appToasterHook";
import { useAppUserDetailsSelector } from "../../_common/hooks/selectors/userSelector";
import { useTranslation } from "src/_common/hooks/useTranslation";
import CurrencySelectionModal from "../modal/CurrencySelectionModal";
import SkillSelectionModal from "../modal/SkillSelectionModal";
import {
  Language,
  WorkPlace,
  JobPostType,
  Skill,
  Country,
  City,
  Currency,
  SalaryRange,
  EducationLevel,
  AIJobFormValues,
  AIJobDescriptionMainProps,
} from "../../_common/interfaces/ApiReqRes";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { ReactSearchAutocomplete } from "react-search-autocomplete";

const createValidationSchema = (descriptionType: number, t: any) =>
  yup.object({
    title: yup.string().required(t("jobCreate.validation.jobTitleRequired")),
    jobDescription:
      // descriptionType === 1
      //   ? yup
      //       .string()
      //       .required(t("jobCreate.validation.jobDescriptionRequired"))
      //       .min(20, t("jobCreate.validation.jobDescriptionMinLength"))
      //       .max(10000, t("jobCreate.validation.jobDescriptionMaxLength"))
      //   :
      yup.string(),
    jobPostType: yup.string().required(t("jobCreate.validation.jobPostTypeRequired")),
    workplace: yup.string().required(t("jobCreate.validation.workplaceRequired")),
    country: yup.string().required(t("jobCreate.validation.countryRequired")),
    jobLocation: yup.string().when("country", {
      is: (country: string) => country && country.trim() !== "",
      then: (schema: yup.StringSchema) => schema.required(t("jobCreate.validation.cityRequired")),
      otherwise: (schema: yup.StringSchema) => schema,
    }),
    educationLevel: yup.string(),
    salary: yup
      .string()
      .required(t("jobCreate.validation.salaryRequired"))
      .matches(/^\d{3,9}$/, t("jobCreate.validation.salaryDigitsValidation")),
    salaryPeriod: yup.string().required(t("jobCreate.validation.salaryPeriodRequired")),
    vacancies: yup
      .number()
      .required(t("jobCreate.validation.vacanciesRequired"))
      .min(1, t("jobCreate.validation.vacanciesMinimum")),
    roleDescription: yup.string(),
    skills: yup.array().of(yup.string()),
    rejectionSettings: yup.string(),
    selectedLanguages: yup.array().of(yup.string()).min(1, t("jobCreate.validation.languagesRequired")),
  });

export default function AIJobDescriptionMain({
  descriptionType,
  jobTitle,
  onBackToStep1,
  onBackToJobList,
}: AIJobDescriptionMainProps) {
  const { t } = useTranslation("job-create");
  const dispatch = useDispatch();
  const history = useHistory();
  const commonApi = useCommonApi();
  const jobPostingApi = useJobPostingApi();
  const toast = useToaster();
  const userDetails = useAppUserDetailsSelector();
  const [activeStep, setActiveStep] = useState(1);
  const [selectedLocationTags, setSelectedLocationTags] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]); // Store skill IDs from API response
  const [aiGeneratedSkills, setAiGeneratedSkills] = useState<string[]>([]); // Skills with is_matched: 0
  const [showAiSkillsSection, setShowAiSkillsSection] = useState(false); // Control AI skills section visibility
  const [customSkills, setCustomSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["English"]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showSkillsDropdown, setShowSkillsDropdown] = useState(false);
  const [selectedEducationLevel, setSelectedEducationLevel] = useState<any | null>(null);
  // const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [showCityInfo, setShowCityInfo] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<any | null>("USD");
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState<string>("$");
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancingField, setEnhancingField] = useState<"jobDescription" | "roleDescription" | "rejectionSettings">(
    "jobDescription",
  );
  const [isGeneratingJobDescription, setIsGeneratingJobDescription] = useState(false);
  const [cityInput, setCityInput] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [selectedGenderId, setSelectedGenderId] = useState<number | null>(null);
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>([]);
  const [selectedNationalityIds, setSelectedNationalityIds] = useState<number[]>([]);
  const [nationalitySearchItems, setNationalitySearchItems] = useState<{ id: string; name: string }[]>([]);
  const [hasNationalityInteracted, setHasNationalityInteracted] = useState(false);
  const [nationalitySelectKey, setNationalitySelectKey] = useState(0);
  const [selectedIndustryId, setSelectedIndustryId] = useState<number | null>(null);
  const [selectedIndustryName, setSelectedIndustryName] = useState<string>("");
  const [hasIndustryInteracted, setHasIndustryInteracted] = useState(false);
  const [industrySelectKey, setIndustrySelectKey] = useState(0);
  const [industryItems, setIndustryItems] = useState<{ id: string; name: string }[]>([]);
  const [selectedLangIds, setSelectedLangIds] = useState<number[]>([]);
  const [selectedLangNames, setSelectedLangNames] = useState<string[]>([]);
  const [langSearchItems, setLangSearchItems] = useState<{ id: string; name: string }[]>([]);
  const [hasLangInteracted, setHasLangInteracted] = useState(false);
  const [langSelectKey, setLangSelectKey] = useState(0);
  const [langInputSearch, setLangInputSearch] = useState<string>("");
  const [pendingRoleDescription, setPendingRoleDescription] = useState<string>("");
  const [pendingRejectionSettings, setPendingRejectionSettings] = useState<string>("");
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [hideSalary, setHideSalary] = useState(false);
  const [minExp, setMinExp] = useState<number>(0);
  const [maxExp, setMaxExp] = useState<number>(0);
  const [contactEmail, setContactEmail] = useState<string>("");

  // Custom validation states for ReactQuill fields
  const [roleDescriptionError, setRoleDescriptionError] = useState<string>("");
  const [rejectionSettingsError, setRejectionSettingsError] = useState<string>("");

  // State to persist form data across steps for preview
  const [persistedFormData, setPersistedFormData] = useState<any>({});
  const [freshJobDetails, setFreshJobDetails] = useState<any>(null);
  const jobListings = useJobListSelector();
  const { jobId } = useParams<{ jobId?: string }>();

  // AI Language selection states
  const [showAiLanguageDropdown, setShowAiLanguageDropdown] = useState(false);
  const [selectedAiLanguage, setSelectedAiLanguage] = useState<string>("EN");
  const [aiLanguageDropdownField, setAiLanguageDropdownField] = useState<
    "jobDescription" | "roleDescription" | "rejectionSettings" | null
  >(null);

  // Redux selectors
  const {
    languages,
    workplaces,
    jobPostTypes,
    skills,
    countriesNew,
    citiesNew,
    currencies,
    salaryRanges,
    educationLevels,
    nationalities,
    industries,
  } = useSelector((state: any) => ({
    languages: state.global.languages,
    workplaces: state.global.workplaces,
    jobPostTypes: state.global.jobPostTypes,
    skills: state.global.skills,
    countriesNew: state.global.countriesNew,
    citiesNew: state.global.citiesNew,
    currencies: state.global.currencies,
    salaryRanges: state.global.salaryRanges,
    educationLevels: state.global.educationLevels,
    nationalities: state.global.nationalities,
    industries: state.global.industries,
  }));

  // Use fallback data if no data from API
  const languagesData = languages && languages.length > 0 ? languages : null;
  const workplacesData = workplaces && workplaces.length > 0 ? workplaces : null;
  const jobPostTypesData = jobPostTypes && jobPostTypes.length > 0 ? jobPostTypes : null;
  const skillsData = skills && skills.length > 0 ? skills : null;
  const countriesData = countriesNew && countriesNew.length > 0 ? countriesNew : null;
  const citiesData = citiesNew && citiesNew.length > 0 ? citiesNew : null;
  const currenciesData = currencies && currencies.length > 0 ? currencies : null;
  const salaryRangesData = salaryRanges && salaryRanges.length > 0 ? salaryRanges : null;
  const educationLevelsData = educationLevels && educationLevels.length > 0 ? educationLevels : null;
  const nationalitiesData = nationalities?.length > 0 ? nationalities : null;
  const industriesData = industries?.length > 0 ? industries : null;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    trigger,
    clearErrors,
    reset,
  } = useForm<AIJobFormValues>({
    resolver: yupResolver(createValidationSchema(descriptionType || 0, t)),
    mode: "onTouched", // Only validate when field is touched or on submit
    defaultValues: {
      title: jobTitle || "",
      jobDescription: "",
      jobPostType: "",
      workplace: "",
      country: "",
      jobLocation: "",
      educationLevel: "",
      salary: "",
      salaryPeriod: "monthly",
      vacancies: 1,
      roleDescription: "",
      skills: [],
      rejectionSettings: "",
      selectedLanguages: ["English"],
    },
  });

  const [countrySelectKey, setCountrySelectKey] = useState(0);
  const [citySelectKey, setCitySelectKey] = useState(0);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [selectedCountryName, setSelectedCountryName] = useState<string>("");
  const [selectedCityName, setSelectedCityName] = useState<string>("");
  // Helper to determine if city dropdown should be disabled
  const isCityDisabled = !selectedCountryId;
  const [countries, setCountries] = useState<Array<{ id: string; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: string; name: string }>>([]);
  const [hasCountryInteracted, setHasCountryInteracted] = useState(false);
  const [hasCityInteracted, setHasCityInteracted] = useState(false);

  // Fetch countries from API
  const getCountries = (searchName: string = "") => {
    const params: any = { limit: 30 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: params,
    });
  };

  // Fetch cities from API based on country (mimic completeProfile)
  const getCities = (countryId: string, searchName: string = "") => {
    if (!countryId) {
      setCities([]);
      return;
    }
    const params: any = { country_id: countryId, limit: 200 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.CITIES_NEW,
      payload: params,
    });
  };

  // Handle country selection (mimic completeProfile)
  const handleCountrySelect = (item: any, setValue: Function) => {
    setValue("country", item.name);
    setSelectedCountryId(item.id);
    setSelectedCountryName(item.name);
    setValue("jobLocation", ""); // Clear city when country changes
    setSelectedCityId(""); // Clear selected city ID when country changes
    setSelectedCityName("");
    setCities([]);
    // Reset interaction and remount to close dropdown
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
    getCities(item.id); // Load cities for selected country

    // Set currency based on selected country
    if (item.currency_code) {
      setSelectedCurrency(item.currency_code);

      // Set currency symbol based on currency code
      const currencySymbolMap: { [key: string]: string } = {
        AED: "د.إ",
        AUD: "A$",
        BDT: "৳",
        BGN: "лв",
        BND: "B$",
        BRL: "R$",
        CAD: "C$",
        CHF: "CHF",
        CNY: "¥",
        CZK: "Kč",
        DKK: "kr",
        EUR: "€",
        GBP: "£",
        HKD: "HK$",
        HUF: "Ft",
        IDR: "Rp",
        ILS: "₪",
        INR: "₹",
        JPY: "¥",
        KRW: "₩",
        KWD: "د.ك",
        MXN: "Mex$",
        MYR: "RM",
        NOK: "kr",
        NZD: "NZ$",
        OMR: "ر.ع.",
        PHP: "₱",
        PKR: "₨",
        PLN: "zł",
        QAR: "ر.ق",
        RON: "lei",
        RUB: "₽",
        SAR: "ر.س",
        SEK: "kr",
        SGD: "S$",
        THB: "฿",
        TRY: "₺",
        TWD: "NT$",
        USD: "$",
        VND: "₫",
        XCD: "EC$",
        ZAR: "R",
      };

      const symbol = currencySymbolMap[item.currency_code] || "$";
      setSelectedCurrencySymbol(symbol);
    }
  };

  // Handle city selection (mimic completeProfile)
  const handleCitySelect = (item: any, setValue: Function) => {
    setValue("jobLocation", item.name);
    setSelectedCityId(item.id);
    setSelectedCityName(item.name);
    // Reset interaction and remount to close dropdown
    setHasCityInteracted(false);
    setCitySelectKey((k) => k + 1);
  };

  // Handle job editing - populate form when jobToEdit is found
  useEffect(() => {
    if (jobId && jobListings?.length > 0) {
      const jobToEdit = jobListings.find((job) => job.emp_job_id == jobId);
      if (jobToEdit) {
        // Set form values from the job data
        setValue("title", jobToEdit.job_title || "");
        setValue("jobDescription", ""); // This would be from the initial description if available

        // For jobPostType, validate against available options
        if (jobToEdit.job_type && jobPostTypesData) {
          console.log("Setting jobPostType. Value from API:", jobToEdit.job_type);
          console.log(
            "Available jobPostType options:",
            jobPostTypesData.map((type: any) => type.name),
          );
          let matchingJobType = jobPostTypesData.find((type: any) => type.name === jobToEdit.job_type);

          // Fallback: try case-insensitive matching
          if (!matchingJobType) {
            matchingJobType = jobPostTypesData.find(
              (type: any) => type.name.toLowerCase() === jobToEdit.job_type.toLowerCase(),
            );
          }

          if (matchingJobType) {
            setValue("jobPostType", matchingJobType.name); // Use the exact option value
            console.log("Successfully set jobPostType to:", matchingJobType.name);
          } else {
            console.warn("Job type not found in options:", jobToEdit.job_type);
            setValue("jobPostType", ""); // Clear invalid value
          }
        } else {
          setValue("jobPostType", "");
        }

        // For workplace, validate against available options
        if (jobToEdit.workplace_type && workplacesData) {
          console.log("Setting workplace. Value from API:", jobToEdit.workplace_type);
          console.log(
            "Available workplace options:",
            workplacesData.map((workplace: any) => workplace.name),
          );
          let matchingWorkplace = workplacesData.find((workplace: any) => workplace.name === jobToEdit.workplace_type);

          // Fallback: try case-insensitive matching
          if (!matchingWorkplace) {
            matchingWorkplace = workplacesData.find(
              (workplace: any) => workplace.name.toLowerCase() === jobToEdit.workplace_type.toLowerCase(),
            );
          }

          if (matchingWorkplace) {
            setValue("workplace", matchingWorkplace.name); // Use the exact option value
            console.log("Successfully set workplace to:", matchingWorkplace.name);
          } else {
            console.warn("Workplace type not found in options:", jobToEdit.workplace_type);
            setValue("workplace", ""); // Clear invalid value
          }
        } else {
          setValue("workplace", "");
        }

        setValue("jobLocation", jobToEdit.city_name || "");

        // Set salary and salary period
        setValue("salary", jobToEdit.salary_range);
        setValue("salaryPeriod", jobToEdit.salary_period || "");

        setValue("vacancies", jobToEdit.no_of_vacancy || 1);

        // Set expiry date
        if (jobToEdit.expiry_date) {
          setExpiryDate(jobToEdit.expiry_date);
        }

        // Set hide salary flag
        if (jobToEdit.hide_salary !== undefined) {
          setHideSalary(jobToEdit.hide_salary === 1);
        }

        // Set experience fields
        if (jobToEdit.min_exp !== undefined) {
          setMinExp(jobToEdit.min_exp || 0);
        }
        if (jobToEdit.max_exp !== undefined) {
          setMaxExp(jobToEdit.max_exp || 0);
        }
        // Set contact email
        if (jobToEdit.contact_email) {
          setContactEmail(jobToEdit.contact_email);
        } else if (userDetails?.company?.recruiter_email && !contactEmail) {
          // Fallback to recruiter_email if no contact_email
          setContactEmail(userDetails.company.recruiter_email);
        }

        // setValue("roleDescription", jobToEdit.job_desc || "");
        const htmlContentJobDesc = convertNewlinesToHtml(jobToEdit.job_desc || "");
        setPendingRoleDescription(htmlContentJobDesc);
        // setValue("rejectionSettings", jobToEdit.application_rejection_message || "");
        const htmlContentRej = convertNewlinesToHtml(jobToEdit.application_rejection_message || "");
        setPendingRejectionSettings(htmlContentRej);

        // Set language flags based on en_flag and ar_flag
        const languages = [];
        if (jobToEdit.en_flag === 1) languages.push("English");
        if (jobToEdit.ar_flag === 1) languages.push("Arabic");
        if (languages.length > 0) {
          setSelectedLanguages(languages);
          setValue("selectedLanguages", languages);
        }

        // Set skills from skill_list
        if (jobToEdit.skill_list) {
          const skillsArray = jobToEdit.skill_list.split(",").map((skill: string) => skill.trim());
          setSelectedSkills(skillsArray);
          setValue("skills", skillsArray);

          // Set skill IDs if available
          if (jobToEdit.job_skills_id && Array.isArray(jobToEdit.job_skills_id)) {
            setSelectedSkillIds(jobToEdit.job_skills_id);
          }
        }

        // Set country and city IDs
        if (jobToEdit.country_id) {
          setSelectedCountryId(jobToEdit.country_id.toString());
          setSelectedCountryName(jobToEdit.country_name || "");
        }

        // Set currency
        if (jobToEdit.currency_name) {
          setSelectedCurrency(jobToEdit.currency_name);

          // Set currency symbol based on currency name
          const currencySymbolMap: { [key: string]: string } = {
            AED: "د.إ",
            AUD: "A$",
            BDT: "৳",
            BGN: "лв",
            BND: "B$",
            BRL: "R$",
            CAD: "C$",
            CHF: "CHF",
            CNY: "¥",
            CZK: "Kč",
            DKK: "kr",
            EUR: "€",
            GBP: "£",
            HKD: "HK$",
            HUF: "Ft",
            IDR: "Rp",
            ILS: "₪",
            INR: "₹",
            JPY: "¥",
            KRW: "₩",
            KWD: "د.ك",
            MXN: "Mex$",
            MYR: "RM",
            NOK: "kr",
            NZD: "NZ$",
            OMR: "ر.ع.",
            PHP: "₱",
            PKR: "₨",
            PLN: "zł",
            QAR: "ر.ق",
            RON: "lei",
            RUB: "₽",
            SAR: "ر.س",
            SEK: "kr",
            SGD: "S$",
            THB: "฿",
            TRY: "₺",
            TWD: "NT$",
            USD: "$",
            VND: "₫",
            XCD: "EC$",
            ZAR: "R",
          };

          const symbol = currencySymbolMap[jobToEdit.currency_name] || "$";
          setSelectedCurrencySymbol(symbol);
        }

        // Set education level
        if (jobToEdit.education_level && educationLevelsData) {
          console.log("Setting educationLevel. Value from API:", jobToEdit.education_level);
          console.log(
            "Available educationLevel options:",
            educationLevelsData.map((level: any) => level.name),
          );
          let matchingEducationLevel = educationLevelsData.find(
            (level: any) => level.name === jobToEdit.education_level,
          );

          // Fallback: try case-insensitive matching
          if (!matchingEducationLevel) {
            matchingEducationLevel = educationLevelsData.find(
              (level: any) => level.name.toLowerCase() === jobToEdit.education_level.toLowerCase(),
            );
          }

          if (matchingEducationLevel) {
            setValue("educationLevel", matchingEducationLevel.name); // Use the exact option value
            setSelectedEducationLevel(matchingEducationLevel); // Set the selected education level state
            console.log("Successfully set educationLevel to:", matchingEducationLevel.name);
          } else {
            console.warn("Education level not found in options:", jobToEdit.education_level);
            setValue("educationLevel", ""); // Clear invalid value
            setSelectedEducationLevel(null);
          }
        } else {
          setValue("educationLevel", "");
          setSelectedEducationLevel(null);
        }

        if (jobToEdit.gender_id) setSelectedGenderId(jobToEdit.gender_id);
        
        // Set nationality - check if array has values (nationality_list may not exist)
        if (Array.isArray(jobToEdit.job_nationality_id) && jobToEdit.job_nationality_id.length) {
          setSelectedNationalityIds(jobToEdit.job_nationality_id);
          if (jobToEdit.nationality_list) {
            const names = jobToEdit.nationality_list.split(",").map((n: string) => n.trim()).filter(Boolean);
            setSelectedNationalities(names);
          }
        }
        // Set industry - check if array has values
        if (Array.isArray(jobToEdit.industry_ids) && jobToEdit.industry_ids.length) {
          const industryId = jobToEdit.industry_ids[0];
          setSelectedIndustryId(industryId);
          if (jobToEdit.industry_list) {
            const industryName = jobToEdit.industry_list.split(",")[0].trim();
            setSelectedIndustryName(industryName);
            setIndustryInputSearch(industryName); // Show in input
            // Initialize industryItems with selected industry so dropdown shows it
            setIndustryItems([{ id: industryId.toString(), name: industryName }]);
            setIndustrySelectKey((k) => k + 1); // Force re-render to show value
          }
        }
        // Set language - check if array has values
        if (Array.isArray(jobToEdit.language_ids) && jobToEdit.language_ids.length) {
          setSelectedLangIds(jobToEdit.language_ids);
          if (jobToEdit.language_list) {
            const names = jobToEdit.language_list.split(",").map((n: string) => n.trim()).filter(Boolean);
            setSelectedLangNames(names);
          }
        }
      }
    }
  }, [jobId, jobListings, jobPostTypesData, workplacesData, salaryRangesData, educationLevelsData]);

  // Auto-fill contact email with recruiter_email for new jobs
  useEffect(() => {
    // Only auto-fill for new jobs (not editing) and when contactEmail is empty
    if (!jobId && !contactEmail && userDetails?.company?.recruiter_email) {
      setContactEmail(userDetails.company.recruiter_email);
    }
  }, [jobId, userDetails?.company?.recruiter_email]);

  // Fetch fresh job details from API when in edit mode
  useEffect(() => {
    if (!jobId) return;
    jobPostingApi.callGetJobDetails(
      { jobId },
      (message: any, response: any) => {
        if (response?.job) {
          setFreshJobDetails(response.job);
        } else if (response?.data?.job) {
          setFreshJobDetails(response.data.job);
        }
      },
      (error: any) => {
        console.error("Error fetching job details for edit:", error);
      }
    );
  }, [jobId]);

  // Populate gender/nationality/industry from fresh API data
  useEffect(() => {
    if (!freshJobDetails) return;
    if (freshJobDetails.gender_id) setSelectedGenderId(freshJobDetails.gender_id);
    
    // Set nationality - check if array has values (nationality_list may not exist)
    if (Array.isArray(freshJobDetails.job_nationality_id) && freshJobDetails.job_nationality_id.length) {
      setSelectedNationalityIds(freshJobDetails.job_nationality_id);
      if (freshJobDetails.nationality_list) {
        const names = freshJobDetails.nationality_list.split(",").map((n: string) => n.trim()).filter(Boolean);
        setSelectedNationalities(names);
      }
    }
    // Set industry - check if array has values
    if (Array.isArray(freshJobDetails.industry_ids) && freshJobDetails.industry_ids.length) {
      const industryId = freshJobDetails.industry_ids[0];
      setSelectedIndustryId(industryId);
      if (freshJobDetails.industry_list) {
        const industryName = freshJobDetails.industry_list.split(",")[0].trim();
        setSelectedIndustryName(industryName);
        setIndustryInputSearch(industryName); // Show in input
        // Initialize industryItems with selected industry so dropdown shows it
        setIndustryItems([{ id: industryId.toString(), name: industryName }]);
        setIndustrySelectKey((k) => k + 1); // Force re-render to show value
      }
    }
    // Set language - check if array has values
    if (Array.isArray(freshJobDetails.language_ids) && freshJobDetails.language_ids.length) {
      setSelectedLangIds(freshJobDetails.language_ids);
      if (freshJobDetails.language_list) {
        const names = freshJobDetails.language_list.split(",").map((n: string) => n.trim()).filter(Boolean);
        setSelectedLangNames(names);
      }
    }
  }, [freshJobDetails]);

  // Watch form values and update Redux store (debounced)
  const watchedValues = watch();
  const roleDescriptionValue = watch("roleDescription");
  const rejectionSettingsValue = watch("rejectionSettings");

  // Debug watchedValues for preview section
  // console.log("watchedValues for preview:", watchedValues);
  // console.log("workplace value:", watchedValues.workplace);
  // console.log("jobPostType value:", watchedValues.jobPostType);
  // console.log("roleDescription value:", watchedValues.roleDescription);

  // Debug roleDescription changes
  useEffect(() => {
    //console.log('roleDescriptionValue changed:', roleDescriptionValue);
  }, [roleDescriptionValue]);

  // Debug rejectionSettings changes
  useEffect(() => {
    //console.log('rejectionSettingsValue changed:', rejectionSettingsValue);
  }, [rejectionSettingsValue]);

  // Force form update when roleDescription changes
  useEffect(() => {
    if (roleDescriptionValue) {
      //console.log('Forcing form update with roleDescription:', roleDescriptionValue);
    }
  }, [roleDescriptionValue]);

  // Handle pending role description when step changes
  useEffect(() => {
    if (activeStep === 2 && pendingRoleDescription) {
      //console.log('Setting pending role description on Step 2:', pendingRoleDescription);
      setValue("roleDescription", pendingRoleDescription, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setPendingRoleDescription(""); // Clear pending state
    }
  }, [activeStep, pendingRoleDescription, setValue]);

  // Handle pending rejection settings when step changes
  useEffect(() => {
    if (activeStep === 2 && pendingRejectionSettings) {
      //console.log('Setting pending rejection settings on Step 2:', pendingRejectionSettings);
      setValue("rejectionSettings", pendingRejectionSettings, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setPendingRejectionSettings(""); // Clear pending state
    }
  }, [activeStep, pendingRejectionSettings, setValue]);

  useEffect(() => {
    // Only update Redux if form has actual data
    if (watchedValues && Object.keys(watchedValues).length > 0) {
      const updateFormData = {
        ...watchedValues,
        selectedLocationTags,
        selectedSkills,
        customSkills,
        selectedCountryId,
        selectedCurrency,
        selectedLanguages,
      };

      // Use a timeout to debounce updates and avoid infinite loops
      const timeoutId = setTimeout(() => {
        dispatch({
          type: SAGA_ACTIONS.JOB_POSTING.UPDATE_FORM_DATA,
          payload: updateFormData,
        });
      }, 100); // 100ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [
    JSON.stringify(watchedValues), // Use JSON.stringify to avoid object reference issues
    selectedLocationTags.join(","),
    selectedSkills.join(","),
    selectedSkillIds.join(","),
    aiGeneratedSkills.join(","),
    customSkills.join(","),
    selectedCountryId,
    selectedCurrency?.id,
    selectedLanguages.join(","),
    dispatch,
  ]);

  // Handle step changes
  const handleStepChange = (newStep: number) => {
    setActiveStep(newStep);
    dispatch({
      type: SAGA_ACTIONS.JOB_POSTING.UPDATE_CURRENT_STEP,
      payload: newStep,
    });
  };

  // // Restore form values from Redux when activeStep changes
  // useEffect(() => {
  //   if (formData && Object.keys(formData).length > 0) {
  //     console.log("Restoring form values from Redux for step:", activeStep, formData);

  //     // Set form values from Redux store without triggering validation
  //     Object.keys(formData).forEach((key) => {
  //       const value = formData[key];
  //       if (value !== undefined && value !== null && value !== '') {
  //         // Only set if the current form value is empty or different
  //         const currentValue = watchedValues[key as keyof AIJobFormValues];
  //         if (!currentValue || currentValue !== value) {
  //           setValue(key as keyof AIJobFormValues, value, { shouldValidate: false, shouldDirty: false });
  //         }
  //       }
  //     });

  //     // Restore additional state from Redux
  //     if (formData.selectedLocationTags && JSON.stringify(formData.selectedLocationTags) !== JSON.stringify(selectedLocationTags)) {
  //       setSelectedLocationTags(formData.selectedLocationTags);
  //     }
  //     if (formData.selectedSkills && JSON.stringify(formData.selectedSkills) !== JSON.stringify(selectedSkills)) {
  //       setSelectedSkills(formData.selectedSkills);
  //     }
  //     if (formData.customSkills && JSON.stringify(formData.customSkills) !== JSON.stringify(customSkills)) {
  //       setCustomSkills(formData.customSkills);
  //     }
  //     if (formData.selectedLanguages && JSON.stringify(formData.selectedLanguages) !== JSON.stringify(selectedLanguages)) {
  //       setSelectedLanguages(formData.selectedLanguages);
  //     }
  //     if (formData.selectedCountryId !== undefined && formData.selectedCountryId !== selectedCountryId) {
  //       setSelectedCountryId(formData.selectedCountryId);
  //     }
  //     if (formData.selectedCurrency && formData.selectedCurrency?.id !== selectedCurrency?.id) {
  //       setSelectedCurrency(formData.selectedCurrency);
  //     }
  //   }
  // }, [activeStep]); // Only re-run when step changes

  // Fetch languages and workplaces on component mount
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.LANGUAGES,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.WORK_PLACES,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.JOB_POST_TYPES,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.SKILLS,
      payload: { limit: 30, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: { limit: 300, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.CURRENCIES,
      payload: { limit: 150, offset: 0 },
    });

    dispatch({
      type: SAGA_ACTIONS.COMMON.SALARY_RANGES,
      payload: { limit: 30, offset: 0 },
    });
    dispatch({
      type: SAGA_ACTIONS.COMMON.EDUCATION_LEVELS,
      payload: { limit: 30, offset: 0 },
    });

    // Nationality and industries now load on demand
  }, [dispatch]);

  // Update form title when jobTitle prop changes
  useEffect(() => {
    if (jobTitle) {
      setValue("title", jobTitle);
    }
  }, [jobTitle, setValue]);

  // Sync selectedLanguages with form
  useEffect(() => {
    setValue("selectedLanguages", selectedLanguages);
  }, [selectedLanguages, setValue]);

  useEffect(() => {
    if (nationalitiesData && nationalitiesData.length > 0) {
      setNationalitySearchItems(nationalitiesData.map((n: any) => ({ id: n.id.toString(), name: n.name })));
      if (nationalityCurrentSearchRef.current) {
        setNationalityInputSearch(nationalityCurrentSearchRef.current);
      }
      // Resolve nationality names from IDs if names are not set
      if (selectedNationalityIds.length > 0 && selectedNationalities.length === 0) {
        const names = selectedNationalityIds.map((id: number) => {
          const found = nationalitiesData.find((n: any) => n.id === id);
          return found ? found.name : null;
        }).filter(Boolean);
        if (names.length > 0) {
          setSelectedNationalities(names);
        }
      }
    }
  }, [nationalitiesData, selectedNationalityIds, selectedNationalities.length]);

  useEffect(() => {
    if (industriesData && industriesData.length > 0) {
      const mappedItems = industriesData.map((i: any) => ({ id: i.id.toString(), name: i.name }));
      // Make sure selected industry is always in items
      if (selectedIndustryId && selectedIndustryName) {
        const exists = mappedItems.find((item: any) => item.id === selectedIndustryId.toString());
        if (!exists) {
          mappedItems.unshift({ id: selectedIndustryId.toString(), name: selectedIndustryName });
        }
      }
      setIndustryItems(mappedItems);
      // Resolve industry name from ID if name is not set
      if (selectedIndustryId && !selectedIndustryName) {
        const found = industriesData.find((i: any) => i.id === selectedIndustryId);
        if (found) {
          setSelectedIndustryName(found.name);
          setIndustryInputSearch(found.name); // Show in input
          setIndustrySelectKey((k) => k + 1); // Force re-render to show value
        }
      }
    }
  }, [industriesData, selectedIndustryId, selectedIndustryName]);

  useEffect(() => {
    if (languages && languages.length > 0) {
      setLangSearchItems(languages.map((l: any) => ({ id: l.id.toString(), name: l.name })));
      // Resolve language names from IDs if names are not set
      if (selectedLangIds.length > 0 && selectedLangNames.length === 0) {
        const names = selectedLangIds.map((id: number) => {
          const found = languages.find((l: any) => l.id === id);
          return found ? found.name : null;
        }).filter(Boolean);
        if (names.length > 0) {
          setSelectedLangNames(names);
        }
      }
    }
  }, [languages, selectedLangIds, selectedLangNames.length]);

  // Fetch nationalities/industries/languages on mount when we have IDs
  useEffect(() => {
    if (selectedNationalityIds.length > 0 && selectedNationalities.length === 0) {
      dispatch({ type: SAGA_ACTIONS.COMMON.NATIONALITIES, payload: { limit: 300 } });
    }
  }, [selectedNationalityIds.length]);

  useEffect(() => {
    if (selectedIndustryId && !selectedIndustryName) {
      dispatch({ type: SAGA_ACTIONS.COMMON.INDUSTRIES, payload: { limit: 300 } });
    }
  }, [selectedIndustryId]);

  useEffect(() => {
    if (selectedLangIds.length > 0 && selectedLangNames.length === 0) {
      dispatch({ type: SAGA_ACTIONS.COMMON.LANGUAGES, payload: { limit: 300 } });
    }
  }, [selectedLangIds.length]);

  const nationalityDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const industryDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const langDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const langCurrentSearchRef = useRef<string>("");
  const nationalityCurrentSearchRef = useRef<string>("");
  const [nationalityInputSearch, setNationalityInputSearch] = useState<string>("");
  const industryCurrentSearchRef = useRef<string>("");
  const [industryInputSearch, setIndustryInputSearch] = useState<string>("");

  const getNationalitiesSearch = (q: string = "") => {
    const params: any = { limit: 50 };
    if (q.trim()) params.q = q.trim();
    dispatch({ type: SAGA_ACTIONS.COMMON.NATIONALITIES, payload: params });
  };

  const loadIndustries = (q: string = "") => {
    const params: any = { limit: 100, offset: 0 };
    if (q.trim()) params.q = q.trim();
    dispatch({ type: SAGA_ACTIONS.COMMON.INDUSTRIES, payload: params });
  };

  const loadLanguages = (q: string = "") => {
    const params: any = { limit: 50 };
    if (q.trim()) params.q = q.trim();
    dispatch({ type: SAGA_ACTIONS.COMMON.LANGUAGES, payload: params });
  };

  // Close city dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".city-autocomplete-container")) {
        setShowCityDropdown(false);
      }
    };

    if (showCityDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showCityDropdown]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".language-dropdown-container")) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showLanguageDropdown]);

  // Close skills dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".skills-dropdown-container")) {
        setShowSkillsDropdown(false);
      }
    };

    if (showSkillsDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showSkillsDropdown]);

  // Close city info tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".info-icon") && !target.closest(".info-tooltip")) {
        setShowCityInfo(false);
      }
    };

    if (showCityInfo) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showCityInfo]);

  // Close currency modal when pressing Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowCurrencyModal(false);
      }
    };

    if (showCurrencyModal) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => document.removeEventListener("keydown", handleEscapeKey);
    }
  }, [showCurrencyModal]);

  // Restore form data when navigating back to step 1 or 2
  useEffect(() => {
    if ((activeStep === 1 || activeStep === 2) && persistedFormData && Object.keys(persistedFormData).length > 0) {
      console.log("=== Restoring form data for step:", activeStep);
      console.log("=== Persisted data:", persistedFormData);

      // Restore form values
      Object.keys(persistedFormData).forEach((key) => {
        const value = persistedFormData[key];
        if (
          value !== undefined &&
          value !== null &&
          key !== "selectedSkills" &&
          key !== "selectedLanguages" &&
          key !== "selectedLocationTags" &&
          key !== "customSkills" &&
          key !== "selectedCountryId" &&
          key !== "selectedCityId" &&
          key !== "selectedCountryName" &&
          key !== "selectedCityName" &&
          key !== "selectedCurrency" &&
          key !== "selectedCurrencySymbol" &&
          key !== "hideSalary" &&
          key !== "selectedSkillIds" &&
          key !== "aiGeneratedSkills" &&
          key !== "selectedEducationLevel"
        ) {
          console.log("Restoring form field:", key, "=", value);
          setValue(key as keyof AIJobFormValues, value, { shouldValidate: false, shouldDirty: false });
        }
      });

      // Restore additional state
      if (persistedFormData.selectedSkills) {
        console.log("Restoring selectedSkills:", persistedFormData.selectedSkills);
        setSelectedSkills(persistedFormData.selectedSkills);
      }
      if (persistedFormData.selectedSkillIds) {
        setSelectedSkillIds(persistedFormData.selectedSkillIds);
      }
      if (persistedFormData.aiGeneratedSkills) {
        setAiGeneratedSkills(persistedFormData.aiGeneratedSkills);
      }
      if (persistedFormData.selectedLanguages) {
        console.log("Restoring selectedLanguages:", persistedFormData.selectedLanguages);
        setSelectedLanguages(persistedFormData.selectedLanguages);
      }
      if (persistedFormData.selectedLocationTags) {
        setSelectedLocationTags(persistedFormData.selectedLocationTags);
      }
      if (persistedFormData.customSkills) {
        setCustomSkills(persistedFormData.customSkills);
      }
      if (persistedFormData.selectedCountryId) {
        console.log("Restoring country:", persistedFormData.selectedCountryId, persistedFormData.selectedCountryName);
        setSelectedCountryId(persistedFormData.selectedCountryId);
        // Load cities for the selected country
        getCities(persistedFormData.selectedCountryId);
      }
      if (persistedFormData.selectedCityId) {
        console.log("Restoring city:", persistedFormData.selectedCityId, persistedFormData.selectedCityName);
        setSelectedCityId(persistedFormData.selectedCityId);
      }
      if (persistedFormData.selectedCountryName) {
        setSelectedCountryName(persistedFormData.selectedCountryName);
      }
      if (persistedFormData.selectedCityName) {
        setSelectedCityName(persistedFormData.selectedCityName);
      }
      if (persistedFormData.selectedCurrency) {
        console.log("Restoring currency:", persistedFormData.selectedCurrency);
        setSelectedCurrency(persistedFormData.selectedCurrency);
      }
      if (persistedFormData.selectedCurrencySymbol) {
        setSelectedCurrencySymbol(persistedFormData.selectedCurrencySymbol);
      }
      if (persistedFormData.hideSalary !== undefined) {
        setHideSalary(persistedFormData.hideSalary);
      }

      // Also restore education level if present
      if (persistedFormData.educationLevel) {
        console.log("Restoring education level:", persistedFormData.educationLevel);
        const educationLevelObj = educationLevelsData?.find(
          (level: any) => level.name === persistedFormData.educationLevel,
        );
        if (educationLevelObj) {
          setSelectedEducationLevel(educationLevelObj);
        }
      }

      console.log("=== Restoration complete ===");
    }
  }, [activeStep]);

  // Helper function to transform form data to API payload
  const transformFormDataToApiPayload = (formData: AIJobFormValues) => {
    console.log("=== Transform Payload Debug Start ===");
    console.log("selectedCountryId:", selectedCountryId);
    console.log("selectedCityId:", selectedCityId);
    console.log("countries array:", countries);
    console.log("cities array:", cities);
    console.log("countriesData:", countriesData);
    console.log("citiesData:", citiesData);

    // Get selected country and city details from our formatted arrays and original Redux data
    const selectedCountryFormatted = countries.find((country) => country.id === selectedCountryId);
    const selectedCityFormatted = cities.find((city) => city.id === selectedCityId);

    // Get full country and city data from Redux for additional fields
    const selectedCountry = countriesData?.find((country: Country) => country.id.toString() === selectedCountryId);
    const selectedCity = citiesData?.find((city: City) => city.id.toString() === selectedCityId);

    console.log("selectedCountryFormatted:", selectedCountryFormatted);
    console.log("selectedCityFormatted:", selectedCityFormatted);
    console.log("selectedCountry (full):", selectedCountry);
    console.log("selectedCity (full):", selectedCity);
    console.log("=== Transform Payload Debug End ===");

    // Get selected workplace and job post type details
    const selectedWorkplace = workplacesData?.find((workplace: any) => workplace.name === formData.workplace);
    const selectedJobPostType = jobPostTypesData?.find((jobType: any) => jobType.name === formData.jobPostType);

    // Get skill IDs from the stored API response
    console.log("DEBUG: Using stored selectedSkillIds from API response:", selectedSkillIds);
    console.log("DEBUG: selectedSkills array:", selectedSkills);

    // Use the stored skill IDs directly from API response instead of looking up
    const finalSkillIds = selectedSkillIds.length > 0 ? selectedSkillIds : [];

    console.log("DEBUG: Final selectedSkillIds for API:", finalSkillIds);

    // Salary data - no need to parse, it's already a number
    const arFlag = selectedLanguages.includes("Arabic") ? 1 : 0;
    const enFlag = selectedLanguages.includes("English") ? 1 : 0;

    console.log("User Data 9: ======== enFlag", enFlag);
    console.log("User Data 10: ======== arFlag", arFlag);

    // Get the actual country and city names for slug generation
    const countryName = selectedCountry?.name || selectedCountryFormatted?.name || formData.country || "";
    const cityName = selectedCity?.name || selectedCityFormatted?.name || formData.jobLocation || "";

    console.log("User Data 11: ======== countryName for slugs", countryName);
    console.log("User Data 12: ======== cityName for slugs", cityName);
    console.log(
      "User Data 13: ======== country_slug",
      selectedCountry?.slug || countryName.toLowerCase().replace(/\s+/g, "-"),
    );
    console.log("User Data 14: ======== city_slug", selectedCity?.slug || cityName.toLowerCase().replace(/\s+/g, "-"));

    // Determine country_id - prioritize selectedCountryId if available
    const countryId = selectedCountryId
      ? parseInt(selectedCountryId)
      : selectedCountryFormatted
        ? parseInt(selectedCountryFormatted.id)
        : selectedCountry?.id || 0;

    return {
      job: {
        ...(cityName
          ? {
              city_id: selectedCityId
                ? parseInt(selectedCityId)
                : selectedCityFormatted
                  ? parseInt(selectedCityFormatted.id)
                  : selectedCity?.id,
              city_name: cityName,
              city_slug: selectedCity?.slug || cityName.toLowerCase().replace(/\s+/g, "-"),
              city_name_ar: selectedCity?.name_ar || cityName,
              city_slug_ar: selectedCity?.slug_ar || cityName.toLowerCase().replace(/\s+/g, "-") + "-ar",
            }
          : {}),
        job_source: 1, // hard coded
        status: 1, // hard coded
        en_flag: enFlag,
        ar_flag: arFlag,
        company_id: userDetails?.user?.company_id || 0, // user id
        company_name: userDetails?.company?.name || "", // company name
        // Country fields with all required variations
        country_id: countryId,
        country_name: countryName,
        country_slug: selectedCountry?.slug || countryName.toLowerCase().replace(/\s+/g, "-"),
        country_name_ar: selectedCountry?.name_ar || countryName,
        country_slug_ar: selectedCountry?.slug_ar || countryName.toLowerCase().replace(/\s+/g, "-") + "-ar",

        posted_type: 1, // hard coded
        created_by_id: userDetails?.user?.id || 0, // user id
        company_post: 1, // hard coded
        job_title: formData.title,
        job_desc: formData.roleDescription || persistedFormData.roleDescription || "", // roleDescription value - prioritize current form data
        job_type: formData.jobPostType,
        job_type_id: selectedJobPostType?.id || 1,
        salary_range: formData.salary,
        salary_period: formData.salaryPeriod,
        currency_name: selectedCurrency || "INR",
        hide_salary: hideSalary ? 1 : 0,
        no_of_vacancy: formData.vacancies,
        min_exp: minExp, // optional, default 0
        max_exp: maxExp, // optional, default 0
        job_experience_range: "", // not required
        contact_email: contactEmail, // optional
        expiry_date: "", // not required
        posted_date: "", // not required
        job_skills_id: finalSkillIds,
        skill_list: selectedSkills.join(","),
        skill_list_ar: selectedSkills.join(","), // not required
        skill_slug_list: selectedSkills.map((skill) => skill.toLowerCase().replace(/\s+/g, "-")).join(","), // not required
        skill_auto_mapping: 1, // hard coded
        application_rejection_message: formData.rejectionSettings || persistedFormData.rejectionSettings || "", // rejectionSettings value - prioritize current form data
        application_rejection_message_ar: formData.rejectionSettings || persistedFormData.rejectionSettings || "", // rejectionSettings value - prioritize current form datajectionSettings || formData.rejectionSettings || "", // rejection_setting value
        // key_responsibilities: [], // Not required
        ...(selectedEducationLevel && selectedEducationLevel.id
          ? {
              education_level_id: selectedEducationLevel.id,
              education_level: selectedEducationLevel.name,
              // education_level_ar: selectedEducationLevel.name_ar || selectedEducationLevel.name,
            }
          : {}),
        workplace_type_id: selectedWorkplace?.id || 1, // workplace id
        workplace_type: formData.workplace, // workplace name
        gender_id: selectedGenderId ?? null,
        job_nationality_id: selectedNationalityIds,
        industry_ids: selectedIndustryId ? [selectedIndustryId] : [],
        language_ids: selectedLangIds,
      },
    };
  };

  const onSubmit = async (data: AIJobFormValues) => {
    console.log("onSubmit called with activeStep:", activeStep);
    console.log("Form data:", data);
    if (activeStep === 1) {
      // Validate Step 1 fields
      const fieldsToValidate = [
        "title",
        "jobPostType",
        "workplace",
        "country",
        "jobLocation",
        "salary",
        "vacancies",
        "selectedLanguages",
      ];

      // Add jobDescription validation if descriptionType is 1
      // if (descriptionType === 1) {
      //   fieldsToValidate.push("jobDescription");
      // }

      // Trigger validation for all Step 1 fields
      const isValid = await trigger(fieldsToValidate as any);

      // Additional validation for radio buttons and dropdowns in edit mode
      if (jobId && (!data.jobPostType || !data.workplace || !data.salary || !data.country)) {
        console.warn("Invalid or missing values detected in edit mode:", {
          jobPostType: data.jobPostType,
          workplace: data.workplace,
          salary: data.salary,
          country: data.country,
        });

        // Show specific error messages for missing values
        if (!data.jobPostType) {
          toast.error(t("jobCreate.validation.selectValidJobType"));
        }
        if (!data.workplace) {
          toast.error(t("jobCreate.validation.selectValidWorkplaceType"));
        }
        if (!data.salary) {
          toast.error(t("jobCreate.validation.salaryRequired"));
        }
        if (!data.country) {
          toast.error(t("jobCreate.validation.selectValidCountry"));
        }

        return;
      }

      if (isValid) {
        // Validate that country and city are actually selected with IDs
        if (!selectedCountryId) {
          toast.error(t("jobCreate.validation.selectCountryFromDropdown"));
          return;
        }

        if (!selectedCityId) {
          toast.error(t("jobCreate.validation.selectCityFromDropdown"));
          return;
        }

        // Check if we're in edit mode (jobId exists)
        const isEditMode = !!jobId;

        // If in edit mode, skip API call and move directly to Step 2
        if (isEditMode) {
          // console.log("Edit mode detected - skipping API call and moving to Step 2");

          // Save form data for preview
          setPersistedFormData({
            ...data,
            selectedSkills,
            selectedLanguages,
            selectedLocationTags,
            customSkills,
            selectedCountryId,
            selectedCountryName,
            selectedCityId,
            selectedCityName,
            selectedCurrency,
            selectedCurrencySymbol,
          });

          setActiveStep(2);
          return;
        }

        // If descriptionType is 1 and jobDescription exists, call the generate API (only for new jobs)
        if (descriptionType === 1 && (data.jobDescription || jobTitle)) {
          setIsGeneratingJobDescription(true);

          // Get selected country and city details
          const selectedCountry = countriesData?.find(
            (country: Country) => country.id.toString() === selectedCountryId,
          );
          const selectedCity = citiesData?.find((city: City) => city.id.toString() === selectedCityId);

          // Get selected workplace and job post type details
          const selectedWorkplace = workplacesData?.find((workplace: any) => workplace.name === data.workplace);
          const selectedJobPostType = jobPostTypesData?.find((jobType: any) => jobType.name === data.jobPostType);
          const selectedEducationLevelFromForm = educationLevelsData?.find(
            (level: any) => level.name === data.educationLevel,
          );

          const requestData: any = {
            text: data.jobDescription || (data.title || jobTitle) + " - Job Description",
            job_title: data.title || jobTitle || "",
            company_name: userDetails?.company?.name || "",
            location:
              selectedCity?.name && selectedCountry?.name
                ? `${selectedCity.name}, ${selectedCountry.name}`
                : selectedCountry?.name || data.jobLocation || "",
            employment_type: selectedJobPostType?.name || data.jobPostType || "",
            // experience_level: selectedEducationLevelFromForm?.name || data.educationLevel || "",
          };
          if (userDetails?.company?.industry) {
            requestData.industry = String(userDetails?.company?.industry || "");
          }
          if (selectedGenderId) {
            requestData.gender = selectedGenderId === 1 ? "Male" : "Female";
          }
          if (selectedNationalities.length > 0) {
            requestData.nationality = selectedNationalities.join(",");
          }
          if (selectedLangNames.length > 0) {
            requestData.known_language = selectedLangNames.join(",");
          }
          if (selectedIndustryName) {
            requestData.industry = selectedIndustryName;
          }
          // Only include salary if hideSalary is not checked
          if (!hideSalary && data.salary) {
            requestData.salary_range = `${selectedCurrency || "USD"} ${data.salary}`;
            requestData.salary_period = data.salaryPeriod;
          }
          // Include experience range - always pass
          requestData.min_exp = minExp;
          requestData.max_exp = maxExp;

          console.log("=== AI Generate API Request ===", requestData);

          commonApi.callGenerateJobDescriptionSkills(
            requestData,
            (message: string, response: any) => {
              // Success callback
              if (response) {
                // Update skills if provided
                if (response.skills && Array.isArray(response.skills)) {
                  // Separate matched and unmatched skills
                  const matchedSkills = response.skills.filter((skill: any) => skill.is_matched === 1);
                  const unmatchedSkills = response.skills.filter((skill: any) => skill.is_matched === 0);

                  // Extract names and IDs for matched skills
                  const matchedSkillNames = matchedSkills.map((skill: any) => skill.name);
                  const matchedSkillIds = matchedSkills
                    .filter((skill: any) => skill.id !== null)
                    .map((skill: any) => skill.id);

                  // Extract names for unmatched skills
                  const unmatchedSkillNames = unmatchedSkills.map((skill: any) => skill.name);

                  // Set matched skills as selected skills
                  setSelectedSkills(matchedSkillNames);
                  setSelectedSkillIds(matchedSkillIds);
                  setValue("skills", matchedSkillNames);
                  dispatch({
                    type: SAGA_ACTIONS.JOB_POSTING.SET_SELECTED_SKILLS,
                    payload: matchedSkillNames,
                  });

                  // Set unmatched skills as AI generated skills
                  setAiGeneratedSkills(unmatchedSkillNames);
                }

                // Update role description if provided
                if (response.role_description_and_responsibilites) {
                  //console.log('Raw API response text:', response.role_description_and_responsibilites);
                  // Convert newlines to HTML paragraphs for ReactQuill
                  const htmlContent = convertNewlinesToHtml(response.role_description_and_responsibilites);
                  //console.log('Converted HTML content:', htmlContent);

                  // Store pending role description to be set when step changes
                  setPendingRoleDescription(htmlContent);
                  //console.log('Stored pending role description:', htmlContent);
                }

                // Update rejection settings if provided
                if (response.rejection_setting) {
                  // console.log('Raw rejection setting from API:', response.rejection_setting);
                  // Store pending rejection settings to be set when step changes
                  setPendingRejectionSettings(response.rejection_setting);
                  // console.log('Stored pending rejection settings:', response.rejection_setting);
                }

                toast.success(t("jobCreate.messages.jobDescriptionGenerated"));
              }

              setIsGeneratingJobDescription(false);
              // Move to Step 2 after API success and form updates
              // console.log("Step 1 job form data:", { ...data, selectedLanguages });

              // Save form data for preview
              setPersistedFormData({
                ...data,
                selectedSkills,
                selectedLanguages,
                selectedLocationTags,
                customSkills,
                selectedCountryId,
                selectedCountryName,
                selectedCityId,
                selectedCityName,
                selectedCurrency,
                selectedCurrencySymbol,
              });

              setActiveStep(2);
            },
            (errorMessage: string) => {
              // Error callback
              toast.error(errorMessage || t("jobCreate.messages.failedToGenerateJobDescription"));
              setIsGeneratingJobDescription(false);
              // Move to Step 2 even if API fails
              // console.log("Step 1 job form data:", { ...data, selectedLanguages });

              // Save form data for preview
              setPersistedFormData({
                ...data,
                selectedSkills,
                selectedLanguages,
                selectedLocationTags,
                customSkills,
                selectedCountryId,
                selectedCountryName,
                selectedCityId,
                selectedCityName,
                selectedCurrency,
                selectedCurrencySymbol,
              });

              setActiveStep(2);
            },
          );
        } else {
          // Move to Step 2 directly if no jobDescription or descriptionType is not 1
          // console.log("Step 1 job form data:", { ...data, selectedLanguages });

          // Save form data for preview
          setPersistedFormData({
            ...data,
            selectedSkills,
            selectedLanguages,
            selectedLocationTags,
            customSkills,
            selectedCountryId,
            selectedCountryName,
            selectedCityId,
            selectedCityName,
            selectedCurrency,
            selectedCurrencySymbol,
          });

          setActiveStep(2);
        }
      }
    } else if (activeStep === 2) {
      // Handle Step 2 submission - validate ReactQuill fields and move to Step 3
      console.log("Step 2 detected - validating fields");

      // Validate Step 2 fields
      const isStep2Valid = validateStep2Fields();

      if (isStep2Valid) {
        console.log("Step 2 validation passed - moving to Step 3");
        console.log("Step 2 job form data:", { ...data, selectedLanguages, selectedSkills });
        setActiveStep(3);
      } else {
        console.log("Step 2 validation failed - staying on Step 2");
        // Form will display the error messages set by validateStep2Fields
      }
    } else {
      // Handle Step 3 (final submission)
      console.log("Final job form submitted:", { ...data, selectedLanguages, selectedSkills });
      // alert("Step 3 triggered! API call starting..."); // Debug alert

      // Validate that country and city are actually selected with IDs
      if (!selectedCountryId) {
        toast.error(t("jobCreate.validation.selectCountryFromDropdown"));
        return;
      }

      if (!selectedCityId) {
        toast.error(t("jobCreate.validation.selectCityFromDropdown"));
        return;
      }

      // Transform form data to API payload
      const apiPayload = transformFormDataToApiPayload({
        ...persistedFormData,
        ...data,
      });

      console.log("API Payload:", apiPayload);
      // alert("API Payload created, calling API..."); // Debug alert

      jobPostingApi.callCreateJob(
        apiPayload,
        (message: string, response: any) => {
          // Success callback
          // console.log("Job created successfully:", response);
          // alert("Job created successfully!"); // Debug alert
          toast.success(t("jobCreate.messages.jobPostedSuccessfully"));

          // Extract emp_job_id from response
          const empJobId = response?.job?.emp_job_id;

          // Set localStorage flag for first-time talent scouting load
          if (empJobId) {
            localStorage.setItem(`firstTimeLoad_${empJobId}`, "true");
          }

          // Redirect to thank you page after a brief delay
          setTimeout(() => {
            history.push(URLS.JOB_POSTING.THANK_YOU("job", empJobId));
          }, 1000); // 1.5 second delay to show the toast
        },
        (errorMessage: string) => {
          // Error callback
          console.error("Failed to create job:", errorMessage);
          // alert("API Error: " + errorMessage); // Debug alert
          toast.error(errorMessage || t("jobCreate.messages.failedToCreateJob"));
        },
      );
    }
  };
  const test_desc = watch("roleDescription");
  // console.log('role description watch:', test_desc);
  const test_rejection = watch("rejectionSettings");
  // console.log('rejection settings watch:', test_rejection);
  // descriptionType
  // console.log('AIJobDescriptionMain descriptionType:', descriptionType);
  // Optional cleanup on component unmount
  useEffect(() => {
    return () => {
      // Save current form state before unmounting (in case user navigates away)
      const currentFormData = watch();
      dispatch({
        type: SAGA_ACTIONS.JOB_POSTING.UPDATE_FORM_DATA,
        payload: {
          ...currentFormData,
          selectedLocationTags,
          selectedSkills,
          selectedSkillIds,
          aiGeneratedSkills,
          customSkills,
          selectedCountryId,
          selectedCurrency,
          selectedLanguages,
        },
      });
    };
  }, []);

  const handleSkillRemove = (skillToRemove: string) => {
    // Find the index of the skill to get its corresponding ID
    const skillIndex = selectedSkills.indexOf(skillToRemove);
    const newSkills = selectedSkills.filter((skill) => skill !== skillToRemove);

    // Remove corresponding skill ID if index exists
    let newSkillIds = [...selectedSkillIds];
    if (skillIndex !== -1 && skillIndex < selectedSkillIds.length) {
      newSkillIds = selectedSkillIds.filter((_, index) => index !== skillIndex);
    }

    setSelectedSkills(newSkills);
    setSelectedSkillIds(newSkillIds);
    setValue("skills", newSkills);
    dispatch({
      type: SAGA_ACTIONS.JOB_POSTING.SET_SELECTED_SKILLS,
      payload: newSkills,
    });
  };

  const handleSkillAdd = (skill: Skill) => {
    if (!selectedSkills.includes(skill.name)) {
      const newSkills = [...selectedSkills, skill.name];
      const newSkillIds = [...selectedSkillIds, skill.id];
      setSelectedSkills(newSkills);
      setSelectedSkillIds(newSkillIds);
      setValue("skills", newSkills);
      dispatch({
        type: SAGA_ACTIONS.JOB_POSTING.SET_SELECTED_SKILLS,
        payload: newSkills,
      });
    }
  };

  const handleAiSkillAdd = (skillName: string) => {
    if (!selectedSkills.includes(skillName)) {
      const newSkills = [...selectedSkills, skillName];
      setSelectedSkills(newSkills);
      setValue("skills", newSkills);
      dispatch({
        type: SAGA_ACTIONS.JOB_POSTING.SET_SELECTED_SKILLS,
        payload: newSkills,
      });

      // Remove from AI generated skills
      setAiGeneratedSkills((prev) => prev.filter((skill) => skill !== skillName));
    }
  };

  const handleAiSkillRemove = (skillName: string) => {
    // Add back to AI generated skills
    if (!aiGeneratedSkills.includes(skillName)) {
      setAiGeneratedSkills((prev) => [...prev, skillName]);
    }
  };

  const handleLanguageAdd = (language: Language) => {
    if (!selectedLanguages.includes(language.name)) {
      const newLanguages = [...selectedLanguages, language.name];
      setSelectedLanguages(newLanguages);
      setValue("selectedLanguages", newLanguages);
      dispatch({
        type: SAGA_ACTIONS.JOB_POSTING.SET_SELECTED_LANGUAGES,
        payload: newLanguages,
      });
    }
    setShowLanguageDropdown(false);
  };

  const handleLanguageRemove = (languageToRemove: string) => {
    // Prevent removing if only 1 language left
    if (selectedLanguages.length <= 1) {
      return;
    }
    const newLanguages = selectedLanguages.filter((lang) => lang !== languageToRemove);
    setSelectedLanguages(newLanguages);
    setValue("selectedLanguages", newLanguages);
    dispatch({
      type: SAGA_ACTIONS.JOB_POSTING.SET_SELECTED_LANGUAGES,
      payload: newLanguages,
    });
  };

  // const availableLanguages = languagesData?.filter((lang: Language) => !selectedLanguages.includes(lang.name)) || [];
  const availableLanguages =
    languagesData?.filter(
      (lang: Language) => !selectedLanguages.includes(lang.name) && ["en", "ar"].includes(lang.code),
    ) || [];

  // Handle cancel button - clear form and go back to step 1 (job title)
  const handleCancel = () => {
    // Reset react-hook-form values to defaults
    reset({
      title: "",
      jobDescription: "",
      jobPostType: "",
      workplace: "",
      country: "",
      jobLocation: "",
      educationLevel: "",
      salary: "",
      salaryPeriod: "monthly",
      vacancies: 1,
      roleDescription: "",
      skills: [],
      rejectionSettings: "",
      selectedLanguages: ["English"],
    });

    // Reset all state variables
    setActiveStep(1);
    setSelectedLocationTags([]);
    setSelectedSkills([]);
    setSelectedSkillIds([]);
    setAiGeneratedSkills([]);
    setShowAiSkillsSection(false);
    setCustomSkills([]);
    setSkillInput("");
    setSelectedLanguages(["English"]);
    setShowLanguageDropdown(false);
    setShowSkillsDropdown(false);
    setSelectedEducationLevel(null);
    setShowCityInfo(false);
    setSelectedCurrency("USD");
    setSelectedCurrencySymbol("$");
    setShowCurrencyModal(false);
    setShowSkillModal(false);
    setIsEnhancing(false);
    setEnhancingField("jobDescription");
    setIsGeneratingJobDescription(false);
    setCityInput("");
    setShowCityDropdown(false);
    setExpiryDate("");
    setFilteredCities([]);
    setPendingRoleDescription("");
    setPendingRejectionSettings("");
    setShowFullAbout(false);
    setHideSalary(false);
    setMinExp(0);
    setMaxExp(0);
    setContactEmail("");
    setRoleDescriptionError("");
    setRejectionSettingsError("");
    setPersistedFormData({});
    setShowAiLanguageDropdown(false);
    setSelectedAiLanguage("EN");
    setAiLanguageDropdownField(null);

    // Reset country and city selects
    setCountrySelectKey((k) => k + 1);
    setCitySelectKey((k) => k + 1);
    setSelectedCountryId("");
    setSelectedCityId("");
    setSelectedCountryName("");
    setSelectedCityName("");
    setCountries([]);
    setCities([]);
    setHasCountryInteracted(false);
    setHasCityInteracted(false);

    // Clear errors
    clearErrors();

    // Call parent's onBackToStep1 to go back to job title step
    if (onBackToStep1) {
      onBackToStep1();
    }
  };

  // Handle direct AI generation without modal
  const handleDirectAiGeneration = (fieldType: "jobDescription" | "roleDescription" | "rejectionSettings") => {
    const currentValue = watch(fieldType);
    const textContent = currentValue
      ? currentValue
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;/g, " ")
          .trim()
      : "";

    if (!textContent.trim()) {
      toast.error(t("jobCreate.validation.enterTextToEnhance"));
      return;
    }

    if (textContent.trim().length < 10) {
      toast.error(t("jobCreate.validation.minimumTenCharacters"));
      return;
    }

    setIsEnhancing(true);
    setEnhancingField(fieldType);

    const requestData = {
      text: textContent,
      text_type:
        fieldType === "jobDescription"
          ? "job_description"
          : fieldType === "roleDescription"
            ? "role_description_and_responsibilites"
            : "rejection_setting",
    };

    commonApi.callEnhanceText(
      requestData,
      (message: string, response: any) => {
        // Success callback
        if (response && response.enhanced_text) {
          // Clear previous content and set new enhanced content
          setValue(fieldType, ""); // Clear first
          setTimeout(() => {
            setValue(fieldType, response.enhanced_text, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }, 100); // Small delay to ensure clearing happens first

          toast.success(
            fieldType === "jobDescription"
              ? t("jobCreate.messages.jobDescriptionEnhancedSuccessfully")
              : fieldType === "roleDescription"
                ? t("jobCreate.messages.roleDescriptionEnhancedSuccessfully")
                : t("jobCreate.messages.rejectionSettingsEnhancedSuccessfully"),
          );
        } else {
          toast.error(t("jobCreate.messages.enhanceTextFailed"));
        }
        setIsEnhancing(false);
      },
      (errorMessage: string) => {
        // Error callback
        toast.error(errorMessage || t("jobCreate.messages.enhanceTextFailed"));
        setIsEnhancing(false);
      },
    );
  };

  // Handle opening AI modal for different fields
  const handleOpenAiModal = (fieldType: "jobDescription" | "roleDescription" | "rejectionSettings") => {
    // Show language dropdown instead of directly calling enhance
    setAiLanguageDropdownField(fieldType);
    setShowAiLanguageDropdown(true);
  };

  // Click outside handler to close language dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        showAiLanguageDropdown &&
        !target.closest(".ai-enhance-wrapper") &&
        !target.closest(".ai-language-dropdown")
      ) {
        setShowAiLanguageDropdown(false);
      }
    };

    if (showAiLanguageDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAiLanguageDropdown]);

  // Handle AI language selection and call AI API
  const handleAiLanguageSelect = (
    languageCode: string,
    fieldType: "jobDescription" | "roleDescription" | "rejectionSettings",
  ) => {
    setShowAiLanguageDropdown(false);
    setSelectedAiLanguage(languageCode);

    // Get the current field value
    const currentValue = watch(fieldType);

    // Check if there's content to enhance
    if (!currentValue || !currentValue.trim()) {
      toast.error(t("jobCreate.validation.enterTextToEnhance"));
      return;
    }

    // For ReactQuill fields, remove HTML tags
    const textContent = currentValue
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    // Check minimum length
    if (textContent.length < 10) {
      toast.error(t("jobCreate.validation.minimumTenCharacters"));
      return;
    }

    // Directly call the API to enhance the text
    setIsEnhancing(true);
    setEnhancingField(fieldType);

    const requestData = {
      text: textContent,
      text_type:
        fieldType === "jobDescription"
          ? "job_description"
          : fieldType === "roleDescription"
            ? "role_description_and_responsibilites"
            : "rejection_setting",
      language: languageCode,
    };

    commonApi.callEnhanceText(
      requestData,
      (message: string, response: any) => {
        // Success callback
        if (response && response.enhanced_text) {
          // Clear previous content and set new enhanced content
          setValue(fieldType, ""); // Clear first
          setTimeout(() => {
            setValue(fieldType, response.enhanced_text, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }, 100); // Small delay to ensure clearing happens first

          toast.success(
            fieldType === "jobDescription"
              ? t("jobCreate.messages.jobDescriptionEnhancedSuccessfully")
              : fieldType === "roleDescription"
                ? t("jobCreate.messages.roleDescriptionEnhancedSuccessfully")
                : t("jobCreate.messages.rejectionSettingsEnhancedSuccessfully"),
          );
        } else {
          toast.error(t("jobCreate.messages.enhanceTextFailed"));
        }
        setIsEnhancing(false);
      },
      (errorMessage: string) => {
        // Error callback
        toast.error(errorMessage || t("jobCreate.messages.enhanceTextFailed"));
        setIsEnhancing(false);
      },
    );
  };

  function convertNewlinesToHtml(text: string) {
    if (!text) return "";

    // Convert double newlines to paragraph breaks, single newlines to <br>
    const paragraphs = text.split("\n\n");
    if (paragraphs.length > 1) {
      // Multiple paragraphs - wrap each in <p> tags
      return paragraphs.map((paragraph) => `<p>${paragraph.trim().replace(/\n/g, "<br>")}</p>`).join("");
    } else {
      // Single paragraph - just replace newlines with <br>
      return text.replace(/\n/g, "<br>");
    }
  }

  // Handle city input change
  const handleCityInputChange = (value: string) => {
    setCityInput(value);
    setShowCityDropdown(true);
  };

  // Helper functions for character counting
  const stripHtmlTags = (html: string): string => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").trim();
  };

  const getPlainTextLength = (html: string): number => {
    return stripHtmlTags(html).length;
  };

  // Custom validation functions for ReactQuill fields
  const validateRoleDescription = (value: string): string => {
    console.log("Validating role description:", value);
    const plainText = stripHtmlTags(value);
    if (!plainText || plainText.trim().length === 0) {
      return t("jobCreate.validation.roleDescriptionRequired");
    }
    if (plainText.length < 300) {
      return t("jobCreate.validation.roleDescriptionMin300");
    }
    if (plainText.length > 10000) {
      return t("jobCreate.validation.roleDescriptionMax10000");
    }
    return "";
  };

  const validateRejectionSettings = (value: string): string => {
    console.log("Validating rejection settings:", value);
    const plainText = stripHtmlTags(value);
    if (!plainText || plainText.trim().length === 0) {
      return t("jobCreate.validation.rejectionSettingsRequired");
    }
    if (plainText.length < 300) {
      return t("jobCreate.validation.rejectionSettingsMin300");
    }
    if (plainText.length > 10000) {
      return t("jobCreate.validation.rejectionSettingsMax10000");
    }
    return "";
  };

  // Validate Step 2 fields
  const validateStep2Fields = (): boolean => {
    const roleDescriptionValue = watch("roleDescription");
    const rejectionSettingsValue = watch("rejectionSettings");

    const roleError = validateRoleDescription(roleDescriptionValue || "");
    const rejectionError = validateRejectionSettings(rejectionSettingsValue || "");

    setRoleDescriptionError(roleError);
    setRejectionSettingsError(rejectionError);
    console.log(!roleError && !rejectionError);
    return !roleError && !rejectionError;
  };

  const publishJobPosting = async (e: any) => {
    e.preventDefault();
    // console.log("Publish Job button clicked directly");
    // console.log("Current selectedSkills state:", selectedSkills);
    // console.log("Current skillsData:", skillsData);
    // alert("Step 3 triggered! API call starting..."); // Debug alert

    // No need to validate Step 2 fields here since validation was already done when transitioning to Step 3
    // The data is already validated and stored in persistedFormData
    console.log("Publishing job with validated data from persistedFormData");

    // Get current form values
    const currentFormData = watch();

    // Transform form data to API payload - prioritize current form data over persisted data only in edit mode
    const apiPayload = transformFormDataToApiPayload({
      ...persistedFormData,
      ...(jobId ? currentFormData : {}), // Only override persistedFormData with current form values in edit mode
      selectedSkills,
      selectedSkillIds,
      aiGeneratedSkills,
      selectedLanguages,
      selectedLocationTags,
      customSkills,
      selectedCountryId,
      selectedCurrency,
      selectedCurrencySymbol,
    });

    //console.log("API Payload:", apiPayload);
    // alert("API Payload created, calling API..."); // Debug alert
    if (jobId) {
      jobPostingApi.callUpdateJob(
        { ...apiPayload, jobId },
        (message: string, response: any) => {
          toast.success(t("jobCreate.messages.jobUpdatedSuccessfully"));
          history.push(URLS.DASHBOARD.MAIN);
        },
        (errorMessage: string) => {
          // Error callback
          console.error("Failed to create job:", errorMessage);
          toast.error(errorMessage || t("jobCreate.messages.failedToCreateJob"));
        },
      );
    } else {
      // Call the job creation API
      console.log("Calling create job API with payload:", apiPayload);
      jobPostingApi.callCreateJob(
        apiPayload,
        (message: string, response: any) => {
          // Success callback
          // console.log("Job created successfully:", response);
          // alert("Job created successfully!"); // Debug alert
          toast.success(t("jobCreate.messages.jobPostedSuccessfully"));

          // Extract emp_job_id from response
          const empJobId = response?.job?.emp_job_id;

          // Set localStorage flag for first-time talent scouting load
          if (empJobId) {
            localStorage.setItem(`firstTimeLoad_${empJobId}`, "true");
          }

          // Redirect to thank you page after a brief delay
          setTimeout(() => {
            history.push(URLS.JOB_POSTING.THANK_YOU("job", empJobId));
          }, 1000); // 1.5 second delay to show the toast
        },
        (errorMessage: string) => {
          // Error callback
          console.error("Failed to create job:", errorMessage);
          // alert("API Error: " + errorMessage); // Debug alert
          toast.error(errorMessage || t("jobCreate.messages.failedToCreateJob"));
        },
      );
    }
  };

  // Load countries on component mount
  useEffect(() => {
    getCountries();
  }, []);

  // Populate countries from Redux state
  useEffect(() => {
    if (countriesNew && countriesNew.length > 0) {
      const formattedCountries = countriesNew.map((country: any) => ({
        id: country.id.toString(),
        name: country.name,
        currency_code: country.currency_code || country.currency || "USD", // Include currency code
      }));
      setCountries(formattedCountries);
    }
  }, [countriesNew]);

  // Populate cities from Redux state
  useEffect(() => {
    if (citiesNew && citiesNew.length > 0) {
      const formattedCities = citiesNew.map((city: any) => ({
        id: city.id.toString(),
        name: city.name,
      }));
      setCities(formattedCities);
    }
  }, [citiesNew]);

  // Effect to set proper values for ReactSearchAutocomplete in edit mode (mimic completeProfile)
  useEffect(() => {
    if (jobId && jobListings?.length > 0) {
      const jobToEdit = jobListings.find((job) => job.emp_job_id == jobId);
      if (jobToEdit) {
        // Set country/city IDs and names from jobToEdit
        if (jobToEdit.country_id) {
          setSelectedCountryId(jobToEdit.country_id.toString());
          setSelectedCountryName(jobToEdit.country_name || "");
          setValue("country", jobToEdit.country_name || "");
          // Fetch cities for the selected country
          getCities(jobToEdit.country_id.toString());
        }
        if (jobToEdit.city_id) {
          setSelectedCityId(jobToEdit.city_id.toString());
          setSelectedCityName(jobToEdit.city_name || "");
          setValue("jobLocation", jobToEdit.city_name || "");
        }
      }
    }
  }, [jobId, jobListings, setValue]);

  const handleNationalitySelect = (item: any) => {
    const id = parseInt(item.id);
    if (!selectedNationalityIds.includes(id)) {
      setSelectedNationalities((prev) => [...prev, item.name]);
      setSelectedNationalityIds((prev) => [...prev, id]);
    }
    nationalityCurrentSearchRef.current = "";
    setNationalityInputSearch("");
    setNationalitySelectKey((k) => k + 1);
  };

  const handleNationalityRemove = (name: string, id: number) => {
    setSelectedNationalities((prev) => prev.filter((n) => n !== name));
    setSelectedNationalityIds((prev) => prev.filter((i) => i !== id));
  };

  const handleIndustrySelect = (item: any) => {
    setSelectedIndustryId(parseInt(item.id));
    setSelectedIndustryName(item.name);
    setHasIndustryInteracted(false);
    setIndustryInputSearch(item.name);
    setIndustrySelectKey((k) => k + 1);
  };

  const handleLanguageSelect = (item: any) => {
    const id = parseInt(item.id);
    if (!selectedLangIds.includes(id)) {
      setSelectedLangNames((prev) => [...prev, item.name]);
      setSelectedLangIds((prev) => [...prev, id]);
    }
    langCurrentSearchRef.current = "";
    setLangInputSearch("");
    setLangSelectKey((k) => k + 1);
  };

  const handleLanguageRemoveMulti = (name: string, id: number) => {
    setSelectedLangNames((prev) => prev.filter((n) => n !== name));
    setSelectedLangIds((prev) => prev.filter((i) => i !== id));
  };

  return (
    <>
      <div className="ai-job-description-main">
        <div className="ai-job-container">
          <div className="ai-job-left">
            {/* Header */}
            <div className="ai-job-header">
              <div className="header-left-ai-job">
                <button className="back-button" onClick={onBackToJobList}>
                  {t("jobCreate.step2.navigation.backToJobList")}
                </button>
                {/* Job Overview Title */}
                <div className="job-overview-section">
                  <h1 className="job-overview-title">{t("jobCreate.step2.header.title")}</h1>
                  <p className="job-overview-subtitle">{t("jobCreate.step2.header.subtitle")}</p>
                </div>
              </div>
              <div className="wrap-lang-exp-grp">
                {/* Expiry Date (Edit Mode Only) */}
                {jobId && expiryDate && (
                  <div className="form-group">
                    <label className="form-label">{t("jobCreate.step2.header.expiresOn")}</label>
                    <div className="expiry-date-display">
                      <i className="fa fa-calendar" aria-hidden="true"></i>
                      <span>{moment(expiryDate).format("DD MMM YYYY")}</span>
                    </div>
                  </div>
                )}

                <div className="language-section">
                  <label className="language-label">{t("jobCreate.step2.additionalInfo.languages.label")}</label>
                  <div
                    className={`language-tags ${errors.selectedLanguages ? "error" : ""}`}
                    style={{
                      background: "#a5c5e5",
                      borderRadius: "30px",
                      gap: "0",
                      height: "45px",
                    }}
                  >
                    <i className="fa fa-globe"></i>
                    {selectedLanguages.map((language, index) => (
                      <span key={index} className="language-tag">
                        {language}
                        {selectedLanguages.length > 1 && (
                          <button type="button" className="tag-remove" onClick={() => handleLanguageRemove(language)}>
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                    {/* <div className="language-dropdown-container">
                        <button
                          type="button"
                          className={`language-dropdown ${showLanguageDropdown ? "active" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowLanguageDropdown(!showLanguageDropdown);
                          }}
                        ></button>
                        {showLanguageDropdown && (
                          <div className="language-dropdown-menu">
                            {availableLanguages.length > 0
                              ? availableLanguages.map((language: Language) => (
                                  <div
                                    key={language.id}
                                    className="language-dropdown-item"
                                    onClick={() => handleLanguageAdd(language)}
                                  >
                                    {language.name}
                                  </div>
                                ))
                              : null}
                          </div>
                        )}
                      </div> */}
                  </div>
                  {errors.selectedLanguages && (
                    <span className="error-text">{t("jobCreate.validation.languagesRequired")}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Steps Navigation */}
            <div className="steps-navigation">
              <div
                className={`step-item ${activeStep === 1 ? "active" : ""}`}
                onClick={() => {
                  // Save current form data before navigating back
                  if (activeStep === 2 || activeStep === 3) {
                    const currentFormData = watch();
                    console.log("=== Saving form data before going to Step 1 ===");
                    console.log("Current form data:", currentFormData);
                    console.log("Additional state:", {
                      selectedSkills,
                      selectedSkillIds,
                      selectedLanguages,
                      selectedCountryId,
                      selectedCountryName,
                      selectedCityId,
                      selectedCityName,
                      selectedCurrency,
                      selectedCurrencySymbol,
                      hideSalary,
                      selectedEducationLevel,
                    });

                    // Only update fields that have actual values (not empty strings)
                    const updatedData: any = { ...persistedFormData };

                    // Update form fields only if they have values
                    Object.keys(currentFormData).forEach((key) => {
                      const value = currentFormData[key as keyof typeof currentFormData];
                      if (value !== undefined && value !== null && value !== "") {
                        updatedData[key] = value;
                      }
                    });

                    // Always update these state variables
                    updatedData.selectedSkills = selectedSkills;
                    updatedData.selectedSkillIds = selectedSkillIds;
                    updatedData.aiGeneratedSkills = aiGeneratedSkills;
                    updatedData.selectedLanguages = selectedLanguages;
                    updatedData.selectedLocationTags = selectedLocationTags;
                    updatedData.customSkills = customSkills;
                    updatedData.selectedCountryId = selectedCountryId;
                    updatedData.selectedCountryName = selectedCountryName;
                    updatedData.selectedCityId = selectedCityId;
                    updatedData.selectedCityName = selectedCityName;
                    updatedData.selectedCurrency = selectedCurrency;
                    updatedData.selectedCurrencySymbol = selectedCurrencySymbol;
                    updatedData.hideSalary = hideSalary;
                    updatedData.selectedEducationLevel = selectedEducationLevel;

                    setPersistedFormData(updatedData);
                    console.log("=== Updated persisted data:", updatedData);
                    console.log("=== Data saved, navigating to Step 1 ===");
                  }
                  setActiveStep(1);
                }}
                style={{ cursor: "pointer" }}
              >
                <div className="step-icon"></div>
                <div className="step-content">
                  <span className="step-number">{t("jobCreate.step2.navigation.step1of2")}</span>
                  <span className="step-title">{t("jobCreate.step2.navigation.basicInfo")}</span>
                </div>
              </div>

              <div
                className={`step-item ${activeStep === 2 ? "active" : ""}`}
                onClick={() => {
                  // Save current form data before navigating
                  if (activeStep === 1 || activeStep === 3) {
                    const currentFormData = watch();
                    console.log("=== Saving form data before going to Step 2 ===");
                    console.log("Current form data:", currentFormData);
                    console.log("Additional state:", {
                      selectedSkills,
                      selectedSkillIds,
                      selectedLanguages,
                      selectedCountryId,
                      selectedCountryName,
                      selectedCityId,
                      selectedCityName,
                      selectedCurrency,
                      selectedCurrencySymbol,
                      hideSalary,
                      selectedEducationLevel,
                    });

                    // Only update fields that have actual values (not empty strings)
                    const updatedData: any = { ...persistedFormData };

                    // Update form fields only if they have values
                    Object.keys(currentFormData).forEach((key) => {
                      const value = currentFormData[key as keyof typeof currentFormData];
                      if (value !== undefined && value !== null && value !== "") {
                        updatedData[key] = value;
                      }
                    });

                    // Always update these state variables
                    updatedData.selectedSkills = selectedSkills;
                    updatedData.selectedSkillIds = selectedSkillIds;
                    updatedData.aiGeneratedSkills = aiGeneratedSkills;
                    updatedData.selectedLanguages = selectedLanguages;
                    updatedData.selectedLocationTags = selectedLocationTags;
                    updatedData.customSkills = customSkills;
                    updatedData.selectedCountryId = selectedCountryId;
                    updatedData.selectedCountryName = selectedCountryName;
                    updatedData.selectedCityId = selectedCityId;
                    updatedData.selectedCityName = selectedCityName;
                    updatedData.selectedCurrency = selectedCurrency;
                    updatedData.selectedCurrencySymbol = selectedCurrencySymbol;
                    updatedData.hideSalary = hideSalary;
                    updatedData.selectedEducationLevel = selectedEducationLevel;

                    setPersistedFormData(updatedData);
                    console.log("=== Updated persisted data:", updatedData);
                    console.log("=== Data saved, navigating to Step 2 ===");
                  }
                  // Only allow navigation to step 2 if step 1 is completed or coming from step 3
                  if (activeStep === 3 || activeStep === 2) {
                    setActiveStep(2);
                  }
                }}
                style={{
                  cursor: activeStep === 3 || activeStep === 2 ? "pointer" : "not-allowed",
                  opacity: activeStep === 1 ? 0.6 : 1,
                }}
              >
                <div className="step-icon"></div>
                <div className="step-content">
                  <span className="step-number">{t("jobCreate.step2.navigation.step2of2")}</span>
                  <span className="step-title">{t("jobCreate.step2.navigation.jobDescription")}</span>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit(onSubmit)} className="ai-job-form">
              {activeStep === 1 ? (
                <>
                  {/* Basic Information Section */}
                  <div className="form-section">
                    <div className="form-section-title-wrap">
                      <h3 className="section-title">{t("jobCreate.step2.basicInfo.title")}</h3>
                      <p className="section-subtitle">{t("jobCreate.step2.basicInfo.subtitle")}</p>
                    </div>
                    {/* Title */}
                    <div className="form-group">
                      <label className="form-label">
                        {t("jobCreate.step2.basicInfo.jobTitle.label")}
                        <span
                          className="info-icon"
                          data-tooltip={t("jobCreate.step2.basicInfo.jobTitle.tooltip")}
                        ></span>
                      </label>

                      <Controller
                        name="title"
                        control={control}
                        render={(renderProps) => (
                          <input
                            onChange={renderProps.onChange}
                            value={renderProps.value}
                            name="title"
                            type="text"
                            placeholder={t("jobCreate.step2.basicInfo.jobTitle.placeholder")}
                            className={`basic-input ${errors.title ? "error" : ""}`}
                            disabled={!!jobId}
                          />
                        )}
                      />
                      {errors.title && <span className="error-text">{errors.title.message}</span>}
                    </div>

                    {/* Describe your Job - Only show when descriptionType is 1 */}
                    {descriptionType === 1 && (
                      <div className="form-group">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.jobDescription.label")}
                          <span
                            className="info-icon"
                            data-tooltip-wide={t("jobCreate.step2.basicInfo.jobDescription.tooltip")}
                            data-tooltip={t("jobCreate.step2.basicInfo.jobDescription.tooltip")}
                          ></span>
                        </label>

                        <Controller
                          name="jobDescription"
                          control={control}
                          render={(renderProps) => {
                            const plainTextLength = getPlainTextLength(renderProps.value || "");
                            const isOverLimit = plainTextLength > 10000;

                            const placeholderText = t("jobCreate.step2.basicInfo.jobDescription.placeholder");

                            return (
                              <div className="rich-text-editor">
                                <ReactQuill
                                  key={placeholderText}
                                  theme="snow"
                                  value={renderProps.value || ""}
                                  onChange={(content) => {
                                    renderProps.onChange(content);
                                  }}
                                  placeholder={placeholderText}
                                  modules={{
                                    toolbar: false,
                                  }}
                                  formats={["header", "bold", "italic", "underline", "list", "bullet", "link"]}
                                  style={{ height: "200px" }}
                                  className={errors.jobDescription ? "error" : ""}
                                />
                                <div className="rich-text-footer">
                                  {!jobId && (
                                    <div
                                      className="ai-generate-btn d-none"
                                      onClick={() => handleDirectAiGeneration("jobDescription")}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <span className="ai-icon"></span>
                                    </div>
                                  )}
                                  <div className={`character-count ${isOverLimit ? "over-limit" : ""}`}>
                                    {plainTextLength}/{t("jobCreate.step2.characterCount")}
                                  </div>
                                </div>
                              </div>
                            );
                          }}
                        />
                        {errors.jobDescription && <span className="error-text">{errors.jobDescription.message}</span>}
                      </div>
                    )}

                    {/* Job Post Type & Workplace */}
                    <div className="form-row">
                      <div className="form-group half-width">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.jobType.label")}
                          <span
                            className="info-icon"
                            data-tooltip={t("jobCreate.step2.basicInfo.jobType.tooltip")}
                          ></span>
                        </label>

                        <div className={`radio-group ${errors.jobPostType ? "error" : ""}`}>
                          <Controller
                            name="jobPostType"
                            control={control}
                            render={(renderProps) => (
                              <div className="radio-options">
                                {jobPostTypesData &&
                                  jobPostTypesData.map((jobType: any, index: number) => (
                                    <label key={index} className="radio-option">
                                      <input
                                        type="radio"
                                        value={jobType.name}
                                        checked={renderProps.value === jobType.name}
                                        onChange={renderProps.onChange}
                                      />
                                      <span>{jobType.name}</span>
                                    </label>
                                  ))}
                              </div>
                            )}
                          />
                        </div>
                        {errors.jobPostType && <span className="error-text">{errors.jobPostType.message}</span>}
                      </div>

                      <div className="form-group half-width">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.workplace.label")}
                          <span
                            className="info-icon"
                            data-tooltip={t("jobCreate.step2.basicInfo.workplace.tooltip")}
                          ></span>
                        </label>

                        <div className={`radio-group ${errors.workplace ? "error" : ""}`}>
                          <Controller
                            name="workplace"
                            control={control}
                            render={(renderProps) => (
                              <div className="radio-options">
                                {workplacesData?.map((workplace: any, index: number) => (
                                  <label key={workplace.id} className="radio-option">
                                    <input
                                      type="radio"
                                      value={workplace.name}
                                      checked={renderProps.value === workplace.name}
                                      onChange={renderProps.onChange}
                                    />
                                    <span>{workplace.name}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          />
                        </div>
                        {errors.workplace && <span className="error-text">{errors.workplace.message}</span>}
                      </div>
                    </div>

                    {/* Education Level */}
                    <div className="form-group">
                      <label className="form-label">
                        {t("jobCreate.step2.additionalInfo.educationLevel.label")}
                        <span
                          className="info-icon"
                          data-tooltip={t("jobCreate.step2.additionalInfo.educationLevel.tooltip")}
                        ></span>
                      </label>

                      <div className={`radio-group ${errors.educationLevel ? "error" : ""}`}>
                        <Controller
                          name="educationLevel"
                          control={control}
                          render={(renderProps) => (
                            <div className="radio-options" style={{ flexDirection: "unset" }}>
                              {educationLevelsData &&
                                educationLevelsData.map((level: EducationLevel) => (
                                  <label key={level.id} className="radio-option">
                                    <input
                                      type="radio"
                                      value={level.name}
                                      checked={renderProps.value === level.name}
                                      onChange={(e) => {
                                        renderProps.onChange(e);
                                        setSelectedEducationLevel(level);
                                      }}
                                    />
                                    <span>{level.name}</span>
                                  </label>
                                ))}
                              <label className="radio-option">
                                <input
                                  type="radio"
                                  value=""
                                  checked={renderProps.value === "" || renderProps.value === null}
                                  onChange={(e) => {
                                    renderProps.onChange(e);
                                    setSelectedEducationLevel(null);
                                  }}
                                />
                                <span>Any</span>
                              </label>
                            </div>
                          )}
                        />
                      </div>
                      {errors.educationLevel && <span className="error-text">{errors.educationLevel.message}</span>}
                    </div>

                    <div className="form-row">
                      <div className="form-group half-width">
                        <label className="form-label">Gender</label>
                        <div className="radio-options">
                          {[{ label: "Any", value: null }, { label: "Male", value: 1 }, { label: "Female", value: 2 }].map((g) => (
                            <label key={g.label} className="radio-option">
                              <input
                                type="radio"
                                name="gender"
                                value={g.value ?? ""}
                                checked={selectedGenderId === g.value}
                                onChange={() => setSelectedGenderId(g.value)}
                              />
                              <span>{g.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="form-row" style={{ position: "relative", zIndex: 11 }}>
                      <div className="form-group half-width">
                        <label className="form-label">Language</label>
                        {selectedLangNames.length > 0 && (
                          <div className="skills-tags" style={{ marginBottom: "8px" }}>
                            {selectedLangNames.map((name, i) => (
                              <span key={i} className="skill-tag">
                                {name}
                                <button type="button" className="remove-skill" onClick={() => handleLanguageRemoveMulti(name, selectedLangIds[i])}>×</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ pointerEvents: "auto" }}>
                          <ReactSearchAutocomplete
                            key={langSelectKey}
                            items={hasLangInteracted ? langSearchItems : []}
                            onSearch={(string) => { langCurrentSearchRef.current = string; setHasLangInteracted(true); if (langDebounceRef.current) clearTimeout(langDebounceRef.current); langDebounceRef.current = setTimeout(() => { loadLanguages(string); }, 300); }}
                            onFocus={() => { if (!hasLangInteracted) { setHasLangInteracted(true); loadLanguages(); } }}
                            onSelect={handleLanguageSelect}
                            inputSearchString={langInputSearch}
                            placeholder="Search language..."
                            showIcon={false}
                            showNoResults={false}
                            autoFocus={false}
                            styling={{
                              height: "45px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              backgroundColor: "white",
                              boxShadow: "none",
                              fontSize: "14px",
                              fontFamily: "inherit",
                              hoverBackgroundColor: "#f3f4f6",
                              color: "#374151",
                              placeholderColor: "#9ca3af",
                              clearIconMargin: "3px 8px 0 0",
                              zIndex: 3,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-row" style={{ position: "relative", zIndex: 10 }}>
                      <div className="form-group half-width">
                        <label className="form-label">Nationality</label>
                        {selectedNationalities.length > 0 && (
                          <div className="skills-tags" style={{ marginBottom: "8px" }}>
                            {selectedNationalities.map((name, i) => (
                              <span key={i} className="skill-tag">
                                {name}
                                <button type="button" className="remove-skill" onClick={() => handleNationalityRemove(name, selectedNationalityIds[i])}>×</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ pointerEvents: "auto" }}>
                          <ReactSearchAutocomplete
                            key={nationalitySelectKey}
                            items={hasNationalityInteracted ? nationalitySearchItems : []}
                            onSearch={(string) => { nationalityCurrentSearchRef.current = string; setHasNationalityInteracted(true); if (nationalityDebounceRef.current) clearTimeout(nationalityDebounceRef.current); nationalityDebounceRef.current = setTimeout(() => { getNationalitiesSearch(string); }, 300); }}
                            onFocus={() => { if (!hasNationalityInteracted) { setHasNationalityInteracted(true); getNationalitiesSearch(); } }}
                            onSelect={handleNationalitySelect}
                            inputSearchString={nationalityInputSearch}
                            placeholder="Search nationality..."
                            showIcon={false}
                            showNoResults={false}
                            autoFocus={false}
                            styling={{
                              height: "45px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              backgroundColor: "white",
                              boxShadow: "none",
                              fontSize: "14px",
                              fontFamily: "inherit",
                              hoverBackgroundColor: "#f3f4f6",
                              color: "#374151",
                              placeholderColor: "#9ca3af",
                              clearIconMargin: "3px 8px 0 0",
                              zIndex: 3,
                            }}
                          />
                        </div>
                      </div>
                      <div className="form-group half-width">
                        <label className="form-label">Industry</label>
                        <ReactSearchAutocomplete
                          key={industrySelectKey}
                          items={hasIndustryInteracted ? industryItems : []}
                          inputSearchString={selectedIndustryName || ""}
                          onSearch={(string) => { industryCurrentSearchRef.current = string; setSelectedIndustryName(string); setHasIndustryInteracted(true); if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current); industryDebounceRef.current = setTimeout(() => { loadIndustries(string); }, 300); }}
                          onFocus={() => { if (!hasIndustryInteracted) { setHasIndustryInteracted(true); loadIndustries(); } }}
                          onSelect={handleIndustrySelect}
                          onClear={() => {
                            setSelectedIndustryId(null);
                            setSelectedIndustryName("");
                            setHasIndustryInteracted(false);
                            industryCurrentSearchRef.current = "";
                            setIndustryInputSearch("");
                            setIndustrySelectKey((k) => k + 1);
                          }}
                          placeholder="Search industry..."
                          showIcon={false}
                          showNoResults={false}
                          autoFocus={false}
                          styling={{
                            height: "45px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            backgroundColor: "white",
                            boxShadow: "none",
                            fontSize: "14px",
                            fontFamily: "inherit",
                            hoverBackgroundColor: "#f3f4f6",
                            color: "#374151",
                            placeholderColor: "#9ca3af",
                            clearIconMargin: "3px 8px 0 0",
                            zIndex: 3,
                          }}
                        />
                      </div>
                    </div>

                    {/* Country & Job Location */}
                    <div className="form-row">
                      <div className="form-group half-width">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.country.label")}
                          <span
                            className="info-icon"
                            data-tooltip={t("jobCreate.step2.basicInfo.country.tooltip")}
                          ></span>
                        </label>
                        <Controller
                          name="country"
                          control={control}
                          render={(renderProps) => (
                            <div
                              className={`autocomplete-container ${errors.country ? "error" : ""}`}
                              onBlur={(e) => {
                                // Check if the blur is moving outside the autocomplete wrapper
                                const currentTarget = e.currentTarget;
                                setTimeout(() => {
                                  if (!currentTarget.contains(document.activeElement)) {
                                    // If there's text in the input but no country selected, clear it
                                    const inputElement = currentTarget.querySelector("input");
                                    if (inputElement && inputElement.value && !selectedCountryName && !jobId) {
                                      renderProps.onChange("");
                                      setHasCountryInteracted(false);
                                      setCountrySelectKey((k) => k + 1);
                                    }
                                  }
                                }, 200);
                              }}
                            >
                              <div style={{ pointerEvents: jobId ? "none" : "auto" }}>
                                <ReactSearchAutocomplete
                                  key={countrySelectKey}
                                  items={hasCountryInteracted && !jobId ? countries : []}
                                  onSearch={(string, results) => {
                                    if (jobId) return; // Disable search in edit mode
                                    setHasCountryInteracted(true);
                                    if (string.length > 0) {
                                      getCountries(string);
                                    } else {
                                      getCountries();
                                    }
                                  }}
                                  onFocus={() => {
                                    if (jobId) return; // Disable focus in edit mode
                                    if (!hasCountryInteracted) {
                                      setHasCountryInteracted(true);
                                      getCountries();
                                    }
                                  }}
                                  onSelect={(item) => {
                                    if (jobId) return; // Disable select in edit mode
                                    handleCountrySelect(item, renderProps.onChange);
                                  }}
                                  onClear={() => {
                                    if (jobId) return; // Disable clear in edit mode
                                    renderProps.onChange("");
                                    setSelectedCountryId("");
                                    setSelectedCountryName("");
                                    setSelectedCityId("");
                                    setSelectedCityName("");
                                    // Clear city field in form when no country is selected
                                    setValue("jobLocation", "");
                                    setCities([]);
                                    setHasCountryInteracted(false);
                                    setCountrySelectKey((k) => k + 1);
                                    getCountries();
                                  }}
                                  placeholder={t("jobCreate.step2.basicInfo.country.placeholder")}
                                  inputSearchString={selectedCountryName || renderProps.value || ""}
                                  showIcon={false}
                                  showNoResults={false}
                                  autoFocus={false}
                                  styling={{
                                    height: "45px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    backgroundColor: jobId ? "#f9fafb" : "white",
                                    boxShadow: "none",
                                    hoverBackgroundColor: jobId ? "#f9fafb" : "#f3f4f6",
                                    color: jobId ? "#9ca3af" : "#374151",
                                    fontSize: "14px",
                                    fontFamily: "inherit",
                                    iconColor: "#6b7280",
                                    lineColor: "#e5e7eb",
                                    placeholderColor: "#9ca3af",
                                    clearIconMargin: "3px 8px 0 0",
                                    zIndex: 2,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                        {errors.country && <span className="error-text">{errors.country.message}</span>}
                      </div>
                      <div className="form-group half-width">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.city.label")}
                          <span className="info-icon" data-tooltip={t("jobCreate.step2.basicInfo.city.tooltip")}></span>
                        </label>
                        <Controller
                          name="jobLocation"
                          control={control}
                          render={(renderProps) => (
                            <div
                              className={`autocomplete-container ${errors.jobLocation ? "error" : ""}`}
                              onBlur={(e) => {
                                // Check if the blur is moving outside the autocomplete wrapper
                                const currentTarget = e.currentTarget;
                                setTimeout(() => {
                                  if (!currentTarget.contains(document.activeElement)) {
                                    // If there's text in the input but no city selected, clear it
                                    const inputElement = currentTarget.querySelector("input");
                                    if (inputElement && inputElement.value && !selectedCityName) {
                                      renderProps.onChange("");
                                      setHasCityInteracted(false);
                                      setCitySelectKey((k) => k + 1);
                                    }
                                  }
                                }, 200);
                              }}
                            >
                              <ReactSearchAutocomplete
                                key={citySelectKey}
                                items={hasCityInteracted ? cities : []}
                                onSearch={(string, results) => {
                                  setHasCityInteracted(true);
                                  if (selectedCountryId) {
                                    if (string.length > 0) {
                                      getCities(selectedCountryId, string);
                                    } else {
                                      getCities(selectedCountryId);
                                    }
                                  }
                                }}
                                onFocus={() => {
                                  if (!hasCityInteracted && selectedCountryId) {
                                    setHasCityInteracted(true);
                                    getCities(selectedCountryId);
                                  }
                                }}
                                onSelect={(item) => handleCitySelect(item, renderProps.onChange)}
                                onClear={() => {
                                  renderProps.onChange("");
                                  setSelectedCityId("");
                                  setSelectedCityName("");
                                  // Close dropdown by resetting interaction and remounting
                                  setHasCityInteracted(false);
                                  setCitySelectKey((k) => k + 1);
                                  if (selectedCountryId) {
                                    getCities(selectedCountryId);
                                  }
                                }}
                                placeholder={
                                  selectedCountryId
                                    ? t("jobCreate.step2.basicInfo.city.placeholderInput")
                                    : t("jobCreate.step2.basicInfo.city.placeholderCountryFirst")
                                }
                                inputSearchString={selectedCityName || renderProps.value || ""}
                                showIcon={false}
                                showNoResults={false}
                                autoFocus={false}
                                styling={{
                                  height: "45px",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "6px",
                                  backgroundColor: selectedCountryId ? "white" : "#f9fafb",
                                  boxShadow: "none",
                                  hoverBackgroundColor: "#f3f4f6",
                                  color: selectedCountryId ? "#374151" : "#9ca3af",
                                  fontSize: "14px",
                                  fontFamily: "inherit",
                                  iconColor: "#6b7280",
                                  lineColor: "#e5e7eb",
                                  placeholderColor: "#9ca3af",
                                  clearIconMargin: "3px 8px 0 0",
                                  zIndex: 1,
                                }}
                              />
                            </div>
                          )}
                        />
                        {errors.jobLocation && <span className="error-text">{errors.jobLocation.message}</span>}
                      </div>
                    </div>

                    {/* Experience (Years) & Contact Email */}
                    <div className="form-row">
                      <div className="form-group half-width">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.experience.label") || "Experience (Years)"}
                          <span
                            className="info-icon"
                            data-tooltip={t("jobCreate.step2.basicInfo.experience.tooltip") || "Minimum and maximum years of experience required"}
                          ></span>
                        </label>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                          <div style={{ flex: 1 }}>
                            <input
                              type="number"
                              min="0"
                              max="50"
                              value={minExp}
                              onChange={(e) => setMinExp(Math.max(0, parseInt(e.target.value) || 0))}
                              className="form-control"
                              placeholder={t("jobCreate.step2.basicInfo.experience.minPlaceholder") || "Min"}
                              style={{ width: "100%" }}
                            />
                          </div>
                          <span style={{ color: "#64748b" }}>to</span>
                          <div style={{ flex: 1 }}>
                            <input
                              type="number"
                              min="0"
                              max="50"
                              value={maxExp}
                              onChange={(e) => setMaxExp(Math.max(0, parseInt(e.target.value) || 0))}
                              className="form-control"
                              placeholder={t("jobCreate.step2.basicInfo.experience.maxPlaceholder") || "Max"}
                              style={{ width: "100%" }}
                            />
                          </div>
                          <span style={{ color: "#64748b" }}>{t("jobCreate.step2.basicInfo.experience.years") || "Years"}</span>
                        </div>
                      </div>

                      <div className="form-group half-width">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.contactEmail.label") || "Contact Email"}
                          <span
                            className="info-icon"
                            data-tooltip={t("jobCreate.step2.basicInfo.contactEmail.tooltip") || "Email address for job inquiries"}
                          ></span>
                        </label>
                        <input
                          type="email"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          className="form-control"
                          placeholder={t("jobCreate.step2.basicInfo.contactEmail.placeholder") || "Enter contact email"}
                        />
                      </div>
                    </div>

                    {/* Salary & Vacancies */}
                    <div className="form-row ">
                      <div className="form-group half-width full-width-mobile">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.salary.label")}
                          <span
                            className="info-icon"
                            data-tooltip-wide={t("jobCreate.step2.basicInfo.salary.tooltip")}
                            data-tooltip={t("jobCreate.step2.basicInfo.salary.tooltip")}
                          ></span>
                        </label>

                        <div style={{ display: "flex", gap: "10px" }}>
                          <Controller
                            name="salary"
                            control={control}
                            render={(renderProps) => (
                              <div style={{ flex: 1, position: "relative" }}>
                                <input
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "").slice(0, 9); // Allow only digits, max length 9
                                    renderProps.onChange(value);
                                  }}
                                  value={renderProps.value}
                                  name="salary"
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={9}
                                  className={`form-control ${errors.salary ? "error" : ""}`}
                                  placeholder={t("jobCreate.step2.basicInfo.salary.placeholder")}
                                  style={{ paddingLeft: "50px" }}
                                />
                                <span
                                  style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#64748b",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {selectedCurrency}
                                </span>
                              </div>
                            )}
                          />{" "}
                          <Controller
                            name="salaryPeriod"
                            control={control}
                            render={(renderProps) => (
                              <select
                                onChange={renderProps.onChange}
                                value={renderProps.value}
                                name="salaryPeriod"
                                className={`basic-select ${errors.salaryPeriod ? "error" : ""}`}
                                style={{ flex: 1 }}
                              >
                                <option value="daily">{t("jobCreate.step2.basicInfo.salary.periods.daily")}</option>
                                <option value="weekly">{t("jobCreate.step2.basicInfo.salary.periods.weekly")}</option>
                                <option value="monthly">{t("jobCreate.step2.basicInfo.salary.periods.monthly")}</option>
                                <option value="yearly">{t("jobCreate.step2.basicInfo.salary.periods.yearly")}</option>
                              </select>
                            )}
                          />
                        </div>

                        {errors.salary && <span className="error-text">{errors.salary.message}</span>}
                        {errors.salaryPeriod && <span className="error-text">{errors.salaryPeriod.message}</span>}

                        <div className="currency-change" style={{ marginTop: "10px" }}>
                          <button
                            type="button"
                            className="currency-btn"
                            onClick={() => {
                              setShowCurrencyModal(true);
                            }}
                          >
                            {selectedCurrency} <span>{t("jobCreate.step2.basicInfo.salary.changeCurrency")}</span>
                          </button>
                        </div>

                        {/* Hide Salary Checkbox */}
                        <div className="checkbox-container" style={{ marginTop: "10px" }}>
                          <label
                            className="checkbox-label"
                            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                          >
                            <input
                              type="checkbox"
                              checked={hideSalary}
                              onChange={(e) => setHideSalary(e.target.checked)}
                              style={{ marginRight: "8px", cursor: "pointer" }}
                            />
                            <span style={{ fontSize: "14px", color: "#374151" }}>
                              {t("jobCreate.step2.basicInfo.salary.hideSalary") || "Hide salary from candidates"}
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="form-group half-width">
                        <label className="form-label">
                          {t("jobCreate.step2.basicInfo.vacancies.label")}
                          <span
                            className="info-icon"
                            data-tooltip={t("jobCreate.step2.basicInfo.vacancies.tooltip")}
                          ></span>
                        </label>

                        <Controller
                          name="vacancies"
                          control={control}
                          render={(renderProps) => (
                            <div className="number-input-container">
                              <button
                                type="button"
                                className="number-btn"
                                onClick={() => renderProps.onChange(Math.max(1, renderProps.value - 1))}
                              >
                                −
                              </button>
                              <input
                                onChange={(e) => renderProps.onChange(parseInt(e.target.value) || 1)}
                                value={renderProps.value}
                                name="vacancies"
                                type="number"
                                min="1"
                                className={`number-input ${errors.vacancies ? "error" : ""}`}
                              />
                              <button
                                type="button"
                                className="number-btn"
                                onClick={() => renderProps.onChange(renderProps.value + 1)}
                              >
                                +
                              </button>
                            </div>
                          )}
                        />
                        {errors.vacancies && <span className="error-text">{errors.vacancies.message}</span>}
                      </div>
                    </div>
                    <div className="form-actions">
                      <button type="button" className="btn-cancel" onClick={handleCancel}>
                        {t("jobCreate.step3.buttons.cancel")}
                      </button>
                      <button type="submit" className="btn-preview" disabled={isGeneratingJobDescription}>
                        {isGeneratingJobDescription
                          ? t("jobCreate.step2.buttons.generateAI")
                          : t("jobCreate.step2.buttons.continue")}
                      </button>
                    </div>
                  </div>
                </>
              ) : activeStep === 2 ? (
                <>
                  {/* Step 2 - Job Description Content */}
                  <div className="form-section">
                    <div className="form-section-title-wrap">
                      <h3 className="section-title">{t("jobCreate.step2.additionalInfo.title")}</h3>
                      <p className="section-subtitle">{t("jobCreate.step2.additionalInfo.subtitle")}</p>
                    </div>

                    {/* Role Description Field */}
                    <div className="form-field">
                      <label className="form-label">
                        {t("jobCreate.step2.additionalInfo.roleDescription.label")}
                        <span
                          className="info-icon"
                          data-tooltip-wide={t("jobCreate.step2.additionalInfo.roleDescription.tooltip")}
                          data-tooltip={t("jobCreate.step2.additionalInfo.roleDescription.tooltip")}
                        ></span>
                      </label>
                      <div
                        style={{
                          marginBottom: "12px",
                          padding: "10px 12px",
                          backgroundColor: "#f0f9ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: "6px",
                          fontSize: "13px",
                          color: "#1e40af",
                          lineHeight: "1.5",
                        }}
                      >
                        <i className="fa fa-info-circle" style={{ marginRight: "6px" }}></i>
                        To get more accurate candidate matches, please provide a clear and detailed job description. Our
                        AI relies on this information to find the best-fit candidates.
                      </div>
                      <Controller
                        name="roleDescription"
                        control={control}
                        render={(renderProps) => {
                          const plainTextLength = getPlainTextLength(renderProps.value || "");
                          const isOverLimit = plainTextLength > 10000;

                          return (
                            <div className="position-relative">
                              <div className="rich-text-editor">
                                <ReactQuill
                                  theme="snow"
                                  value={renderProps.value || ""}
                                  onChange={(content) => {
                                    renderProps.onChange(content);
                                    // Clear error on change
                                    if (roleDescriptionError) {
                                      setRoleDescriptionError("");
                                    }
                                  }}
                                  placeholder={t("jobCreate.step2.additionalInfo.roleDescription.placeholder")}
                                  modules={{
                                    toolbar: [
                                      ["bold", "italic", "underline"],
                                      [{ list: "ordered" }, { list: "bullet" }],
                                    ],
                                  }}
                                  formats={["header", "bold", "italic", "underline", "list", "bullet", "link"]}
                                  style={{ height: "450px" }}
                                  className={roleDescriptionError ? "error" : ""}
                                />
                                <div className="rich-text-footer">
                                  <div className={`character-count ${isOverLimit ? "over-limit" : ""}`}>
                                    {plainTextLength}/{t("jobCreate.step2.characterCount")}
                                  </div>
                                </div>
                              </div>
                              {!jobId && (
                                <div className="ai-language-dropdown-container">
                                  <div
                                    className="ai-enhance-wrapper"
                                    id="ai-generate-role-description-btn"
                                    onClick={() => handleOpenAiModal("roleDescription")}
                                  >
                                    <div className="ai-generate-btn">
                                      <span className="ai-icon"></span>
                                    </div>
                                    <span
                                      style={{
                                        fontSize: "14px",
                                        color:
                                          isEnhancing && enhancingField === "roleDescription" ? "#9ca3af" : "#374151",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {isEnhancing && enhancingField === "roleDescription"
                                        ? t("jobCreate.aiModal.enhancing")
                                        : watch("roleDescription") && watch("roleDescription").trim()
                                          ? "Regenerate with AI"
                                          : "Generate with AI"}
                                    </span>

                                    {/* Language Dropdown */}
                                    {showAiLanguageDropdown && aiLanguageDropdownField === "roleDescription" && (
                                      <div className="ai-language-dropdown">
                                        <div className="lang-wrap">
                                          {[
                                            { code: "EN", name: "English" },
                                            { code: "AR", name: "Arabic" },
                                            { code: "FR", name: "Français" },
                                            { code: "ES", name: "Español" },
                                            { code: "HI", name: "हिन्दी" },
                                          ].map((lang) => (
                                            <div
                                              key={lang.code}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAiLanguageSelect(lang.code, "roleDescription");
                                              }}
                                              className="lang-list"
                                            >
                                              <div className="lang-list-inner">
                                                <span className="lang-code">{lang.code}</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                      {roleDescriptionError && <span className="error-text">{roleDescriptionError}</span>}
                      <p className="character-limit">{t("jobCreate.step2.characterLimit")}</p>
                    </div>

                    {/* Skills Field */}
                    <div className="form-field">
                      <div className="skills-container">
                        <label className="form-label">
                          {t("jobCreate.step2.additionalInfo.skills.label")}
                          <span
                            className="info-icon"
                            data-tooltip-wide={t("jobCreate.step2.additionalInfo.skills.tooltip")}
                            data-tooltip={t("jobCreate.step2.additionalInfo.skills.tooltip")}
                          ></span>
                        </label>
                        {/* Skills Tags */}
                        {selectedSkills.length > 0 ? (
                          <div className="skills-tags">
                            {selectedSkills.map((skill, index) => (
                              <span key={index} className="skill-tag">
                                {skill}
                                <button type="button" className="remove-skill" onClick={() => handleSkillRemove(skill)}>
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <div className="d-flex gap-2 wrap-job-skills">
                          {/* Skills Modal Button */}
                          <div className="skills-dropdown-container">
                            <button type="button" className="btn-cancel" onClick={() => setShowSkillModal(true)}>
                              <i className="fa fa-plus" aria-hidden="true"></i>
                              {t("jobCreate.step2.additionalInfo.skills.addSkills")}
                            </button>
                          </div>

                          {/* AI Skills Generator Section */}

                          {/* {aiGeneratedSkills.length > 0 && (
                            <div className="ai-skills-generator-section">
                              <button
                                type="button"
                                className="ai-skills-btn"
                                onClick={() => setShowAiSkillsSection(!showAiSkillsSection)}
                              >
                                <span className="ai-icon">✨</span>
                                {t("jobCreate.step2.additionalInfo.skills.aiGenerator")} */}
                          {/* <span className="toggle-icon">{showAiSkillsSection ? '▼' : '▶'}</span> */}
                          {/* </button>
                            </div>
                          )} */}
                        </div>
                        {showAiSkillsSection && (
                          <div className="ai-skills-info-container">
                            {aiGeneratedSkills.length > 0 && (
                              <div>
                                {/* <label className="form-label">AI Generated Skills (Suggestions)</label> */}
                                <div className="skills-tags ai-generated-skill">
                                  {aiGeneratedSkills.map((skill, index) => (
                                    <span key={index} className="skill-tag ai-skill">
                                      {skill}
                                      <button
                                        type="button"
                                        className="add-skill d-none"
                                        onClick={() => handleAiSkillAdd(skill)}
                                        title="Add to selected skills"
                                      >
                                        {" "}
                                        +
                                      </button>
                                    </span>
                                  ))}
                                </div>
                                <button className="apply-suggestions-btn d-none">
                                  <span className="plus-icon">+</span>{" "}
                                  {t("jobCreate.step2.additionalInfo.skills.applySuggestions")}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Rejection Settings Field */}
                    <div className="form-field">
                      <label className="form-label">
                        {t("jobCreate.step2.additionalInfo.rejectionSettings.label")}
                        <span
                          className="info-icon"
                          data-tooltip-wide={t("jobCreate.step2.additionalInfo.rejectionSettings.tooltip")}
                          data-tooltip={t("jobCreate.step2.additionalInfo.rejectionSettings.tooltip")}
                        ></span>
                      </label>
                      <Controller
                        name="rejectionSettings"
                        control={control}
                        render={(renderProps) => {
                          const plainTextLength = getPlainTextLength(renderProps.value || "");
                          const isOverLimit = plainTextLength > 10000;

                          return (
                            <div className="rich-text-editor">
                              <ReactQuill
                                theme="snow"
                                value={renderProps.value || ""}
                                onChange={(content) => {
                                  renderProps.onChange(content);
                                  // Clear error on change
                                  if (rejectionSettingsError) {
                                    setRejectionSettingsError("");
                                  }
                                }}
                                placeholder={t("jobCreate.step2.additionalInfo.rejectionSettings.placeholder")}
                                modules={{
                                  toolbar: [
                                    ["bold", "italic", "underline"],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                  ],
                                }}
                                formats={["header", "bold", "italic", "underline", "list", "bullet", "link"]}
                                style={{ height: "200px" }}
                                className={rejectionSettingsError ? "error" : ""}
                              />
                              <div className="rich-text-footer">
                                {!jobId && (
                                  <div
                                    className="ai-generate-btn"
                                    id="ai-generate-rejection-settings-btn"
                                    onClick={() => handleDirectAiGeneration("rejectionSettings")}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <span className="ai-icon">✨</span>
                                  </div>
                                )}
                                <div className={`character-count ${isOverLimit ? "over-limit" : ""}`}>
                                  {plainTextLength}/{t("jobCreate.step2.characterCount")}
                                </div>
                              </div>
                            </div>
                          );
                        }}
                      />
                      {rejectionSettingsError && <span className="error-text">{rejectionSettingsError}</span>}
                      <p className="character-limit">{t("jobCreate.step2.characterLimit")}</p>
                    </div>

                    {/* Form Actions */}
                    <div className="form-actions">
                      <button type="button" className="btn-cancel" onClick={() => setActiveStep(1)}>
                        {t("jobCreate.step2.buttons.back")}
                      </button>
                      <button
                        type="button"
                        className="btn-preview"
                        onClick={(e: any) => {
                          if (jobId) {
                            publishJobPosting(e);
                            return;
                          }
                          // console.log('Preview Job button clicked');
                          // console.log('Current activeStep:', activeStep);
                          if (activeStep === 2) {
                            // Validate Step 2 fields before proceeding
                            const isStep2Valid = validateStep2Fields();

                            if (!isStep2Valid) {
                              console.log("Step 2 validation failed - cannot proceed to preview");
                              return; // Exit early if validation fails
                            }

                            // Save current form data before moving to Step 3
                            const currentFormData = watch();
                            setPersistedFormData({
                              ...persistedFormData, // Keep existing Step 1 data
                              ...currentFormData, // Add Step 2 data
                              selectedSkills,
                              selectedSkillIds,
                              aiGeneratedSkills,
                              selectedLanguages,
                              selectedLocationTags,
                              customSkills,
                              selectedCountryId,
                              selectedCountryName,
                              selectedCityId,
                              selectedCityName,
                              selectedCurrency,
                              selectedCurrencySymbol,
                              hideSalary,
                              selectedEducationLevel,
                            });
                            // console.log('Moving to Step 3');
                            // console.log('Saved form data:', currentFormData);
                            // console.log('Existing persisted data:', persistedFormData);
                            setActiveStep(3);
                          }
                        }}
                      >
                        {jobId ? t("jobCreate.step2.buttons.updateJob") : t("jobCreate.step2.buttons.previewJob")}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="ai-job-description-main">
                  <div className="preview-about-company preview-about-company-top-panel">
                    <div className="preview-about-company-logo d-none">
                      <img
                        src={userDetails?.company?.logo_url || "/images/company-logo.jpg"}
                        alt="Company Logo"
                        width="100%"
                      />
                    </div>
                    <div className="preview-about-company-cont">
                      <div className="preview-about-company-title preview-about-top">
                        <div>
                          <h3>{persistedFormData?.title}</h3>
                          <h4>at {userDetails?.company?.name}.</h4>
                        </div>
                        {/* <a href="#" className="share-btn"><i className="share-icon"></i></a> */}
                      </div>
                      <ul className="preview-about-company-list">
                        <li>
                          <i className="loc-icon"></i>
                          {persistedFormData.selectedCityName || persistedFormData.jobLocation},{" "}
                          {persistedFormData.selectedCountryName || persistedFormData.country} (
                          {persistedFormData.workplace})
                        </li>
                        {!persistedFormData.hideSalary && persistedFormData.salary && (
                          <li>
                            <i className="wallet-icon"></i>
                            {persistedFormData.selectedCurrency}
                            {persistedFormData.salary} {persistedFormData.salaryPeriod}
                          </li>
                        )}
                        <li>
                          <i className="time-icon"></i>
                          {t("jobCreate.step3.preview.postedToday")}
                        </li>
                        <li>
                          <i className="vacancy-icon"></i>
                          {t("jobCreate.step3.preview.vacancy")}: {persistedFormData.vacancies}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="preview-main-panel">
                    <div className="preview-content-block">
                      <h3>{t("jobCreate.step3.preview.roleDescriptionTitle")}</h3>
                    </div>
                    <div className="preview-content-block">
                      {persistedFormData.roleDescription ? (
                        <div dangerouslySetInnerHTML={{ __html: persistedFormData.roleDescription }} />
                      ) : (
                        <p>{t("jobCreate.step3.preview.noRoleDescription")}</p>
                      )}
                    </div>

                    <div className="preview-content-block">
                      <h3>{t("jobCreate.step3.preview.workplace")}</h3>
                      <p>{persistedFormData.workplace || t("jobCreate.step3.preview.notSpecified")}</p>
                    </div>
                    <div className="preview-content-block">
                      <h3>{t("jobCreate.step3.preview.jobPostType")}</h3>
                      <p>{persistedFormData.jobPostType || t("jobCreate.step3.preview.notSpecified")}</p>
                    </div>
                    {persistedFormData.selectedSkills && persistedFormData.selectedSkills.length > 0 && (
                      <div className="preview-content-block">
                        <h3>{t("jobCreate.step3.preview.keySkills")}</h3>
                        <div className="preview-key-skill-tags-wrapper">
                          {persistedFormData.selectedSkills && persistedFormData.selectedSkills.length > 0 ? (
                            persistedFormData.selectedSkills.map((skill: string, index: number) => (
                              <div key={index} className="preview-key-skill-tag">
                                {skill}
                              </div>
                            ))
                          ) : (
                            <p>{t("jobCreate.step3.preview.noSkills")}</p>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="preview-about-company">
                      {userDetails?.company?.logo_url && (
                        <div className="preview-about-company-logo">
                          <img
                            src={userDetails?.company?.logo_url || "/images/company-logo.jpg"}
                            alt="Company Logo"
                            width="100%"
                          />
                        </div>
                      )}
                      <div className="preview-about-company-cont">
                        <div className="preview-about-company-title">
                          <h3>{t("jobCreate.step3.preview.aboutCompany")}</h3>
                          <Link to={`${URLS.AUTH.VIEW_PROFILE}?view=company`} className="outline-btn" target="_blank">
                            <i className="preview-eye"></i>
                            {t("jobCreate.step3.preview.viewProfile")}
                          </Link>
                        </div>
                        {(() => {
                          const about = (userDetails?.company?.about || "").trim();
                          const truncated = about.slice(0, 500);
                          const displayText = showFullAbout ? about : about.length > 500 ? truncated + "..." : about;

                          return (
                            <>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: displayText,
                                }}
                              />
                              {about.length > 500 && !showFullAbout && (
                                <div className="readmore-link">
                                  <a onClick={() => setShowFullAbout(true)}>{t("jobCreate.step3.preview.readMore")}</a>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="form-actions justify-content-start mt-2">
                      <button type="button" className="btn-preview btn-blue" onClick={(e: any) => publishJobPosting(e)}>
                        {t("jobCreate.step3.buttons.publishJob")}
                      </button>
                      <button type="button" className="btn-cancel" onClick={() => setActiveStep(2)}>
                        {t("jobCreate.step2.buttons.back")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Right Sidebar */}
          <div className="ai-job-right d-none">
            <div className="progress-circle">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="8"
                  strokeDasharray="339.29"
                  strokeDashoffset="237.5"
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="progress-text">{t("jobCreate.sidebar.progressPercent")}</div>
            </div>

            <h3 className="sidebar-title">{t("jobCreate.sidebar.title")}</h3>
            <p className="sidebar-subtitle">{t("jobCreate.sidebar.subtitle")}</p>

            <div className="sidebar-image">
              <img src="/images/new-job-right-image.jpg" alt="Job posting illustration" />
            </div>

            <div className="sidebar-info">
              <h4 className="info-title">{t("jobCreate.sidebar.infoTitle")}</h4>
              <p className="info-text">{t("jobCreate.sidebar.infoText")}</p>
            </div>
          </div>
        </div>

        {/* Currency Selection Modal */}
        {showCurrencyModal && (
          <CurrencySelectionModal
            isOpen={showCurrencyModal}
            onClose={() => setShowCurrencyModal(false)}
            currenciesData={currenciesData}
            selectedCurrency={selectedCurrency}
            onCurrencySelect={(currency, currencySymbol) => {
              setSelectedCurrency(currency);
              setSelectedCurrencySymbol(currencySymbol);
            }}
          />
        )}

        {/* Skill Selection Modal */}
        {showSkillModal && (
          <SkillSelectionModal
            isOpen={showSkillModal}
            onClose={() => setShowSkillModal(false)}
            skillsData={skillsData || []}
            selectedSkills={selectedSkills}
            onSkillSelect={handleSkillAdd}
          />
        )}
      </div>
    </>
  );
}
